import { useEffect, useState, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import * as EL from "esri-leaflet";
import pointOnSurface from "@turf/point-on-surface";
import parquesIndustriales from "../services/parquesService";
import { formatKey } from "../utils/formatters";
import { makeShape, COLORS_PARQUES, COLORS_LOTES_PARQUES, COLORS_ZONIFICACION_PARQUES, COLORS_LOTES, COLORS_ZONIFICACION, COLORS_ZONAS_INDUSTRIALES, HALO_LAYER_CONFIG } from "../utils/styleUtils.js";
import { zoomToBoundsAndOpen } from "../utils/mapZoom";
import { createHighlightLayerManager } from "./HighLightLayer.js";
import './popup-styles.css';
// radius and shapeHtml fixed in previous chunks if possible, here I'll focus on the reported lines.
const HALO_RADIUS_METERS = 1800;

// =========================
// Gradient fills para los <path> SVG de parques/zonas. Cada color hex genera
// un <linearGradient> diagonal de un tono ~30 puntos más claro al color base
// de la leyenda. Los gradients viven en un <svg><defs/></svg> oculto en el
// body, referenciado por id desde el atributo `fill` de cada path
// (`fill="url(#px-grad-XXXXXX)"`). Caching por color: cada hex único genera
// el gradient una sola vez en toda la sesión.
// =========================
const _gradientFillCache = new Map();
let _gradientDefsEl = null;

const _ensureGradientDefs = () => {
  if (_gradientDefsEl) return _gradientDefsEl;
  if (typeof document === "undefined") return null;
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "0");
  svg.setAttribute("height", "0");
  svg.setAttribute("aria-hidden", "true");
  svg.style.position = "absolute";
  svg.style.pointerEvents = "none";
  svg.style.overflow = "hidden";
  const defs = document.createElementNS(NS, "defs");

  // Filtro de inner-shadow para el hover de los polígonos. SVG no soporta
  // `box-shadow: inset` desde CSS — la única forma es un <filter>. Se
  // referencia desde CSS via `filter: url(#px-inner-shadow)`.
  // Algoritmo: SourceAlpha → blur → offset → restamos la alpha original
  // (operator out) para quedarnos solo con el borde interno; tintamos
  // ese borde de negro semi-transparente y lo mergeamos sobre la pieza
  // original (SourceGraphic).
  const filter = document.createElementNS(NS, "filter");
  filter.setAttribute("id", "px-inner-shadow");
  filter.setAttribute("x", "-20%");
  filter.setAttribute("y", "-20%");
  filter.setAttribute("width", "140%");
  filter.setAttribute("height", "140%");

  const feBlur = document.createElementNS(NS, "feGaussianBlur");
  feBlur.setAttribute("in", "SourceAlpha");
  feBlur.setAttribute("stdDeviation", "3");
  feBlur.setAttribute("result", "blur");

  const feOffset = document.createElementNS(NS, "feOffset");
  feOffset.setAttribute("in", "blur");
  feOffset.setAttribute("dx", "0");
  feOffset.setAttribute("dy", "2");
  feOffset.setAttribute("result", "offsetBlur");

  // Composite OUT vs SourceAlpha → solo queda el "anillo" interno donde
  // antes había forma. Es lo que se ve como inner shadow.
  const feComposite = document.createElementNS(NS, "feComposite");
  feComposite.setAttribute("in", "offsetBlur");
  feComposite.setAttribute("in2", "SourceAlpha");
  feComposite.setAttribute("operator", "arithmetic");
  feComposite.setAttribute("k2", "-1");
  feComposite.setAttribute("k3", "1");
  feComposite.setAttribute("result", "innerShadow");

  // Tinte oscuro semi-transparente para la sombra interna.
  const feFlood = document.createElementNS(NS, "feFlood");
  feFlood.setAttribute("flood-color", "#000");
  feFlood.setAttribute("flood-opacity", "0.55");
  feFlood.setAttribute("result", "color");

  const feMaskComposite = document.createElementNS(NS, "feComposite");
  feMaskComposite.setAttribute("in", "color");
  feMaskComposite.setAttribute("in2", "innerShadow");
  feMaskComposite.setAttribute("operator", "in");
  feMaskComposite.setAttribute("result", "tinted");

  // Mergeamos source original + sombra interna encima.
  const feMerge = document.createElementNS(NS, "feMerge");
  const node1 = document.createElementNS(NS, "feMergeNode");
  node1.setAttribute("in", "SourceGraphic");
  const node2 = document.createElementNS(NS, "feMergeNode");
  node2.setAttribute("in", "tinted");
  feMerge.appendChild(node1);
  feMerge.appendChild(node2);

  filter.appendChild(feBlur);
  filter.appendChild(feOffset);
  filter.appendChild(feComposite);
  filter.appendChild(feFlood);
  filter.appendChild(feMaskComposite);
  filter.appendChild(feMerge);
  defs.appendChild(filter);

  svg.appendChild(defs);
  document.body.appendChild(svg);
  _gradientDefsEl = defs;
  return defs;
};

const _normalizeHex = (hex) => {
  if (typeof hex !== "string") return null;
  let h = hex.trim();
  if (!h.startsWith("#")) return null;
  h = h.slice(1);
  // Soporta 3 (RGB), 4 (RGBA), 6 (RRGGBB) y 8 (RRGGBBAA). En los formatos
  // con alpha la descartamos — el gradient hereda la fillOpacity del path,
  // no necesita el alpha embedded en el color.
  if (h.length === 3) {
    h = h.split("").map((c) => c + c).join("");
  } else if (h.length === 4) {
    h = h.slice(0, 3).split("").map((c) => c + c).join("");
  } else if (h.length === 8) {
    h = h.slice(0, 6);
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return h.toLowerCase();
};

const _lightenHex = (hex6, amt) => {
  const r = Math.min(255, parseInt(hex6.slice(0, 2), 16) + amt);
  const g = Math.min(255, parseInt(hex6.slice(2, 4), 16) + amt);
  const b = Math.min(255, parseInt(hex6.slice(4, 6), 16) + amt);
  const toHex = (n) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const getGradientFill = (color) => {
  const hex6 = _normalizeHex(color);
  // Para colores no-hex (named, rgba, gradientes existentes) caemos al
  // color literal — sin gradient pero todo sigue pintando.
  if (!hex6) return color;

  const id = `px-grad-${hex6}`;
  if (_gradientFillCache.has(id)) return `url(#${id})`;

  const defs = _ensureGradientDefs();
  if (!defs) return color;

  const NS = "http://www.w3.org/2000/svg";
  const lg = document.createElementNS(NS, "linearGradient");
  lg.setAttribute("id", id);
  lg.setAttribute("x1", "0%");
  lg.setAttribute("y1", "0%");
  lg.setAttribute("x2", "100%");
  lg.setAttribute("y2", "100%");

  // Stop 0: versión clara del color (lighten ~32 por canal). No queda casi
  // blanco — sólo lo suficiente para que el degradé se note.
  const stopLight = document.createElementNS(NS, "stop");
  stopLight.setAttribute("offset", "0%");
  stopLight.setAttribute("stop-color", _lightenHex(hex6, 32));

  // Stop 1: color base de la leyenda. El usuario ve el color "oficial" en
  // la esquina inferior derecha del polígono, el tono claro en la superior
  // izquierda — efecto luz natural de arriba a la izquierda.
  const stopBase = document.createElementNS(NS, "stop");
  stopBase.setAttribute("offset", "100%");
  stopBase.setAttribute("stop-color", `#${hex6}`);

  lg.appendChild(stopLight);
  lg.appendChild(stopBase);
  defs.appendChild(lg);
  _gradientFillCache.set(id, true);
  return `url(#${id})`;
};
const HALO_MAX_ZOOM_TO_SHOW = 8;
const HALO_DELTA_IN_HIDE = 5;
const HALO_DELTA_OUT_SHOW = 5;//1.6; 
const GRID_SIZE_DEGREES = 1.5;

// =========================
// Cache tuning
// =========================
// ArcGIS search query (featureCollection) cache
const CACHE_TTL_SEARCH_MS = 2 * 60 * 1000; // 2 minutes
// Popup/backend data cache
const CACHE_TTL_POPUP_MS = 10 * 60 * 1000; // 10 minutes
// Max cached ArcGIS queries (LRU)
const SEARCH_QUERY_CACHE_MAX = 40;

const searchBoundsCache = new Map(); // key -> { exp, bounds:{sw:{lat,lng}, ne:{lat,lng}} }
const searchQueryCache = new Map(); // key -> { exp, fc }
const searchInFlight = new Map(); // key -> Promise

const popupCache = new Map(); // key -> { exp, data }
const popupInFlight = new Map(); // key -> Promise

const cacheGet = (map, key) => {
  const v = map.get(key);
  if (!v) return null;
  if (typeof v.exp === "number" && Date.now() > v.exp) {
    map.delete(key);
    return null;
  }
  return v;
};

const lruTouch = (map, key, value) => {
  // Map keeps insertion order; re-insert to mark as most-recent
  map.delete(key);
  map.set(key, value);
  while (map.size > SEARCH_QUERY_CACHE_MAX) {
    const oldestKey = map.keys().next().value;
    map.delete(oldestKey);
  }
};

// lruTrim removed because it was unused

const cacheSet = (map, key, payload, ttlMs) => {
  const v = { ...payload, exp: Date.now() + ttlMs };
  if (map === searchQueryCache) {
    lruTouch(map, key, v);
  } else {
    map.set(key, v);
  }
};

const cachedFetch = async (cacheMap, inflightMap, key, fetcher, ttlMs) => {
  const hit = cacheGet(cacheMap, key);
  if (hit && "data" in hit) return hit.data;

  if (inflightMap.has(key)) return inflightMap.get(key);

  const p = (async () => {
    const data = await fetcher();
    cacheSet(cacheMap, key, { data }, ttlMs);
    inflightMap.delete(key);
    return data;
  })().catch((e) => {
    inflightMap.delete(key);
    throw e;
  });

  inflightMap.set(key, p);
  return p;
};
const ArcGISFeatureLayer = ({
  url,
  where = "1=1",
  activeTab,
  layerId = 0,
  layerType,
  style = (feature) => ({ color: "#3388ff", weight: 2, features: feature }),
  propiedades = (feature) => feature.properties || {},
  popupTemplate = (feature, layerId) => {
    const props = feature.properties || {};
    return `
      <b>${props.NOMBRE || "Sin nombre"}</b><br/>ID: ${props.OBJECTID || ""}
    `;
  },
  onLayerLoad,
  onLayerError,
  // Cuando es false la capa se construye igual pero NO se agrega al mapa —
  // los togglees de visibilidad (addTo/removeLayer) son instantáneos y no
  // dispara re-fetch. Usado por MapWithOutSidebar para que los checkboxes
  // de "Capas a mostrar" no re-consulten al servidor.
  visible = true,
  // Callback que recibe el feature cuando el usuario clickea "Ver detalle"
  // dentro de un popup summary. Mismo patrón que Mercados: el popup
  // resume, el modal en RightPanel muestra el detalle completo.
  onShowDetail,
}) => {

  const map = useMap();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const highlight = useRef(null);
  const searchZoomDoneRef = useRef(false);
  const lastSearchTermRef = useRef("");
  const filterZoomDoneRef = useRef(false);
  const lastWhereRef = useRef("");

  // Derivamos SQL (rawWhere) y texto del prop `where` ANTES del effect para
  // poder ponerlos por separado en las deps. Esto es clave para el caching:
  // si solo cambia `__text` (usuario tipea), NO queremos rebuildar las capas
  // ni re-consultar a ArcGIS — la búsqueda corre sobre features ya cargadas.
  const isWhereObj = typeof where === "object" && where !== null;
  const sqlWhere = isWhereObj ? (where.__rawWhere || "1=1") : (where || "1=1");
  const searchTextRaw = isWhereObj ? (where.__text || "") : "";

  // Ref del texto actual — la usa el load handler (que está dentro del effect
  // grande pero se ejecuta de forma asíncrona cuando esri-leaflet termina de
  // cargar features). Sin esto, el handler quedaría con un texto stale del
  // momento en que se construyó la capa.
  const searchTextRef = useRef(searchTextRaw);
  useEffect(() => {
    searchTextRef.current = searchTextRaw;
  }, [searchTextRaw]);

  // Funciones de búsqueda-zoom registradas por cada sublayer durante load.
  // El effect de texto las invoca cuando solo cambia el texto, sin rebuildar.
  const searchZoomFnsRef = useRef([]);

  const halosRef = useRef(null);
  const halosBuiltRef = useRef(false);
  const halosDismissedRef = useRef(false);

  // Refs para soporte de `visible`: layerGroupsRef guarda los grupos
  // creados por el efecto grande; visibleRef permite que el efecto grande
  // lea el valor actual sin tener que estar en sus deps (lo cual lo
  // forzaría a re-construir las capas en cada toggle).
  const layerGroupsRef = useRef([]);
  const visibleRef = useRef(visible);
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  // Identidad estable por instancia para nombrar el pane de Leaflet.
  // Usamos pane CSS display:none/'' para ocultar capas sin hacer
  // map.removeLayer — eso último gatilla onRemove en esri-leaflet y
  // cuando vuelves a addLayer re-corre tile loading (los 7 requests
  // que se veían por toggle). Con pane-hiding, el estado interno
  // de esri-leaflet queda intacto, cero red al togglear.
  const instanceIdRef = useRef(
    `arcgis-${Math.random().toString(36).slice(2, 10)}`
  );

  const initialZoomRef = useRef(null);
  const zoomHandlerRef = useRef(null);

  const zonasZoomDoneRef = useRef(false);
  const firstMoveAfterTab3Ref = useRef(false);

  const popupParLotIndDetalle = (feature) => {
    const p = feature?.properties ?? {};
    const mz = p.mz ?? "-";
    const lote = p.lote ?? "-";
    const titulo =
      lote !== "-" ? `Mz. ${mz} · Lote ${lote}` : `Manzana ${mz}`;
    const safeTitulo = String(titulo)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return `
      <div class="mki-popup" style="--c: #ac0000;">
        <span class="mki-popup-pill">Lote Industrial</span>
        <h3 class="mki-popup-title">${safeTitulo}</h3>
        <button type="button" class="mki-popup-cta" data-action="lote-ver-detalle">
          Ver detalle <span aria-hidden>→</span>
        </button>
      </div>
    `;
  };

  // Popup summary para Parques Industriales — reusa el look-and-feel del
  // popup de Mercados (.mki-popup-*). Click en el CTA dispara onShowDetail,
  // que abre el modal completo en RightPanel.
  const popupParqIndustrialesDetalle = (feature) => {
    const props = feature?.properties || {};
    const nombre = props.valor_descripcion || props.name || "Parque industrial";
    // Escape mínimo para evitar romper el HTML si el nombre trae < / >.
    const safeNombre = String(nombre)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return `
      <div class="mki-popup" style="--c: #6D28D9;">
        <span class="mki-popup-pill">Parque Industrial</span>
        <h3 class="mki-popup-title">${safeNombre}</h3>
        <button type="button" class="mki-popup-cta" data-action="parque-ver-detalle">
          Ver detalle <span aria-hidden>→</span>
        </button>
      </div>
    `;
  };

  // Summary popup para Zona Industrial — mismo look-and-feel que parques.
  // El detalle completo (datos + galería) se abre en el modal de RightPanel
  // al hacer click en "Ver detalle".
  const popupParqZonasIndusDetalle = (feature) => {
    const props = feature?.properties || {};
    // Tomamos el nombre del feature directamente. Cuando viene en formato
    // "ZONA INDUSTRIAL DE TAL. ALGO MAS", nos quedamos con la parte antes del
    // primer punto (igual que tooltip line 551).
    const raw =
      props.valor_descripcion?.split(".")?.[0]?.trim() ||
      props.valor_descripcion ||
      props.name ||
      "Zona industrial";
    const safeNombre = String(raw)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return `
      <div class="mki-popup" style="--c: #a12323;">
        <span class="mki-popup-pill">Zona Industrial</span>
        <h3 class="mki-popup-title">${safeNombre}</h3>
        <button type="button" class="mki-popup-cta" data-action="zona-ver-detalle">
          Ver detalle <span aria-hidden>→</span>
        </button>
      </div>
    `;
  };

  // Summary popup para Zonas Económicas Especiales — mismo look-and-feel
  // que el resto. El detalle completo se abre en el modal de RightPanel
  // (DetalleZonaEconomicaEspecial) al clickear "Ver detalle".
  const popupParqZonasEspDetalle = (feature) => {
    const props = feature?.properties || {};
    const raw =
      props.valor_descripcion ||
      props.name ||
      "Zona Económica Especial";
    const safeNombre = String(raw)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .trim();
    return `
      <div class="mki-popup" style="--c: #00748a;">
        <span class="mki-popup-pill">Zona Económica Especial</span>
        <h3 class="mki-popup-title">${safeNombre}</h3>
        <button type="button" class="mki-popup-cta" data-action="zed-ver-detalle">
          Ver detalle <span aria-hidden>→</span>
        </button>
      </div>
    `;
  };

  const popupParqZonificacionIndusDetalle = (feature) => {
    const p = feature?.properties ?? {};
    const mz = p.mz || p.valor_descripcion || "-";
    const safeMz = String(mz)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return `
      <div class="mki-popup" style="--c: #5e014e;">
        <span class="mki-popup-pill">Zonificación Industrial</span>
        <h3 class="mki-popup-title">Mz. ${safeMz}</h3>
        <button type="button" class="mki-popup-cta" data-action="zonificacion-ver-detalle">
          Ver detalle <span aria-hidden>→</span>
        </button>
      </div>
    `;
  };

  // FlyTo a Lima al entrar al tab Zonas vive ahora en MapWithOutSidebar
  // (efecto sobre filteredData/activeTab) junto con las animaciones de
  // los otros tabs. Antes había uno acá con setTimeout 250ms que peleaba
  // con el de MapWithOutSidebar haciendo que la cámara oscilara y nunca
  // llegara a Lima. El ref se conserva por si algún otro consumidor del
  // archivo lo necesita.

  useEffect(() => {
    if (!map) return;

    if (!map.getPane("halo-pane")) {
      map.createPane("halo-pane");
      const pane = map.getPane("halo-pane");
      pane.style.zIndex = 650;
    }

    if (!halosRef.current) {
      halosRef.current = L.layerGroup().addTo(map);
    }

    return () => {
      if (map && halosRef.current && map.hasLayer(halosRef.current)) {
        map.removeLayer(halosRef.current);
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map || !halosRef.current) return;

    halosRef.current.clearLayers();
    halosBuiltRef.current = false;
    halosDismissedRef.current = false;
    initialZoomRef.current = null;
  }, [activeTab, map]);

  useEffect(() => {
    if (activeTab !== 3) {
      zonasZoomDoneRef.current = false;
    }
  }, [activeTab]);

  useEffect(() => {
    if (!highlight.current && map) {
      highlight.current = createHighlightLayerManager(map);
    }

    return () => {
      highlight.current?.destroy?.();
      highlight.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (!url) {
      setError("URL no proporcionada");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const layerIds = Array.isArray(layerId) ? layerId : [layerId];
      const newLayers = [];
      const allGroups = [];

      const baseWhere = sqlWhere;
      // Limpiamos el registro de search-zoom callbacks del build anterior
      // antes de reconstruir las capas (cada featureLayer registrará el suyo
      // en su load handler más abajo).
      searchZoomFnsRef.current = [];

      const whereKey = String(baseWhere || "1=1");
      if (whereKey !== lastWhereRef.current) {
        lastWhereRef.current = whereKey;
        filterZoomDoneRef.current = false;
      }




      // ============================================
      // Variables de control de zoom
      // ============================================
      let pendingLoads = layerIds.length;
      let firstValidBounds = null;

      // ============================================
      // Función utilitaria para obtener bounds
      // ============================================
      const getSafeBounds = (fl, group) => {
        if (fl?.getBounds && fl.getBounds()) {
          const b = fl.getBounds();
          if (b && b.isValid && b.isValid()) return b;
        }
        const fg = L.featureGroup();
        Object.values(fl?._layers ?? {}).forEach((lyr) => {
          try {
            if (lyr.getBounds) fg.addLayer(lyr);
            else if (lyr.getLatLng) fg.addLayer(L.marker(lyr.getLatLng()));
          } catch { }
        });
        const b2 = fg.getBounds?.();
        if (b2 && b2.isValid && b2.isValid()) return b2;
        if (group?.getBounds) {
          const b3 = group.getBounds();
          if (b3 && b3.isValid && b3.isValid()) return b3;
        }
        return null;
      };


      layerIds.forEach((id) => {
        const finalWhere = baseWhere;

        // Pane único por sublayer. La estrategia para visible toggle es
        // ocultar el pane (display:none) en vez de removeLayer, así
        // esri-leaflet no re-corre tile loading al re-prender.
        const paneName = `${instanceIdRef.current}-${id}`;
        // z-index por sublayer — base 405 (entre overlayPane=400 y
        // markerPane=600). Para zonas industriales (activeTab=3) imponemos
        // un orden explícito: Unidades Inmobiliarias (id=0) arriba, Tipo
        // Zonificación (id=1) en medio, Capa Zonas Industriales (id=2)
        // abajo — así los lotes pequeños no quedan tapados por la zona
        // grande de fondo. Para otros tabs queda en 405 (default).
        let paneZIndex = 405;
        if (activeTab === 3) {
          if (id === 0) paneZIndex = 407;       // Unidades Inmobiliarias
          else if (id === 1) paneZIndex = 406;  // Tipo Zonificación
          else if (id === 2) paneZIndex = 405;  // Capa Zonas Industriales
        }
        if (!map.getPane(paneName)) {
          map.createPane(paneName);
        }
        map.getPane(paneName).style.zIndex = paneZIndex;
        // Estado inicial del pane según visible.
        map.getPane(paneName).style.display = visibleRef.current ? "" : "none";

        // SVG renderer ligado al pane — los polígonos se renderean como
        // <path> SVG real. Esto permite drop-shadow, hover-elevation y otros
        // efectos CSS que en canvas no son posibles. Para los volúmenes
        // típicos de parques/zonas (~decenas a cientos de features por
        // sublayer) la performance es indistinguible de canvas.
        const svgRenderer = L.svg({ padding: 0.5, pane: paneName });

        // El layerGroup SIEMPRE se agrega al mapa, también cuando visible=
        // false. Esto hace pre-fetch: la sublayer carga sus features en
        // background aunque el pane esté oculto, así cuando el usuario
        // cambia de tab o prende su checkbox, las features ya están en
        // memoria. La visibilidad real se controla con pane.style.display.
        const layerGroup = L.layerGroup().addTo(map);
        layerGroup._arcgisLayerId = id;
        layerGroup._arcgisPaneName = paneName;
        allGroups.push(layerGroup);
        const featureLayer = EL.featureLayer({
          url: `${url}/${id}`,
          where: finalWhere,
          renderer: svgRenderer,
          pane: paneName,

          style: (feature) => {
            const p = feature?.properties ?? {};
            // por si lo necesitas luego

            let c = { ...COLORS_LOTES["Se desconoce"] };
            if (activeTab === 3) {

              if (id === 0) {

                const baseUso = p.id_uso
                  ? COLORS_LOTES[p.id_uso] || COLORS_LOTES["Se desconoce"]
                  : COLORS_LOTES["Se desconoce"];

                c = { ...baseUso };

                if (p.id_ocupacion === 2) {
                  c.color = "#808080";
                  c.fillColor = "#FFFF";
                }

              } else if (id === 1) {
                const baseZonif = p.id_zonificacion
                  ? COLORS_ZONIFICACION[p.id_zonificacion] || COLORS_ZONIFICACION["Se desconoce"]
                  : COLORS_ZONIFICACION["Se desconoce"];

                c = { ...baseZonif };
              }

            } else if (activeTab === 2) {

              if (id === 4) {
                const estadoVenta = (p.estado_venta ?? "").trim();
                const baseUso = p.idUso
                  ? COLORS_LOTES_PARQUES[p.idUso] || COLORS_LOTES_PARQUES["Se desconoce"]
                  : COLORS_LOTES_PARQUES["Se desconoce"];

                c = { ...baseUso };

                // Solo este feature se pinta distinto si está DISPONIBLE
                if (estadoVenta.trim().toUpperCase() === "DISPONIBLE" || estadoVenta.trim().toUpperCase() === "DISPONIBLE - SIN HABILITAR") {
                  c.color = "#808080";
                  c.fillColor = "#FFFF";
                }

              } else if (id === 3) {
                const baseZonif = p.zonificacion
                  ? COLORS_ZONIFICACION_PARQUES[p.zonificacion] || COLORS_ZONIFICACION_PARQUES["Se desconoce"]
                  : COLORS_ZONIFICACION_PARQUES["Se desconoce"];

                c = { ...baseZonif };

              } else if (id === 2) {
                c = { ...COLORS_PARQUES[6] };

              } else if (id === 0 || id === 1) {
                c = { ...COLORS_PARQUES[p.id_zonificacion] };
              }
            }

            if (c) {
              return {
                color: c.color,
                weight: 1.2,
                opacity: 0.95,
                // El fill es un linearGradient (claro→base) cacheado por
                // color. Misma referencia entre renders del mismo color, así
                // SVG no recrea defs. Para colores no-hex (p.ej. blanco
                // "#FFFF" del fallback DISPONIBLE) cae al color literal.
                fillColor: getGradientFill(c.fillColor),
                // fillOpacity al 1 — color sólido, sin translucidez.
                fillOpacity: 1,
                // className aplicada a cada <path> SVG. La usamos desde CSS
                // para drop-shadow ambiente y hover-elevation. Si la capa es
                // de fondo (id 0 en zonas, 2 en parques) la marcamos como
                // --base para que tenga sombra más sutil.
                className: `px-arcgis-shape px-arcgis-shape--tab${activeTab} px-arcgis-shape--id${id}`,
              };
            }

            return typeof style === "function" ? style(feature) : style;
          },

          onEachFeature: (feature, layer) => {

            if (activeTab === 2 || activeTab === 3) {
              highlight?.current?.attachHighlight(layer);
            }

            layer.on("popupopen", async (e) => {
              const popupEl = e.popup.getElement();
              const fetchedDiv = popupEl.querySelector("#fetched-data");

              if (!fetchedDiv) return;
              fetchedDiv.innerHTML = "<em>Cargando datos...</em>";
              const cod = feature.properties.id_intervencion;

              if (!cod) {
                fetchedDiv.innerHTML = "<p>No se encontró el código de intervención.</p>";
                return;
              }

              try {
                const response = await cachedFetch(popupCache, popupInFlight, `parques|getParquesIndustriales|${cod}`, () => parquesIndustriales.getParquesIndustriales(cod), CACHE_TTL_POPUP_MS);

                const html = Object.entries(response.items[0]).filter(([key, value]) => value !== null && value !== "-" && value !== "")
                  .map(([key, value]) => `<tr>
                                        <td style="padding:6px 10px; font-weight:bold; color:#fff; background:#ac0000; border-bottom:1px solid #ddd;">
                                          ${formatKey(key)}
                                        </td>
                                        <td style="padding:6px 10px; border-bottom:1px solid #ddd;">
                                          ${value}
                                        </td>
                                      </tr>`).join("");

                fetchedDiv.innerHTML = `
                  <div style="max-height:300px; overflow:auto; font-family:sans-serif;">
                    <table style="border-collapse:collapse; width:100%; font-size:13px;">
                      <tbody>
                        ${html}
                      </tbody>
                    </table>
                  </div>`;
              } catch (err) {
                // console.error("Error fetching data:", err);
                fetchedDiv.innerHTML = "<p>Error al cargar la información.</p>";
              }
            });

            switch (layerType) {
              case "parques":
                layer.bindTooltip(feature.properties.nombre || "Sin nombre");
                break;
              case "parquesWFS":
                layer.bindTooltip(feature.properties.valor_descripcion ? feature.properties.valor_descripcion : ("Mz: " + feature.properties.mz + " - Lote: " + feature.properties.lote) || "Sin nombres");
                break;
              default:
                layer.bindTooltip(
                  feature.properties.valor_descripcion?.split(".")[0]?.trim() ||
                  feature.properties.valor_descripcion ||
                  feature.properties.Name ||
                  "Sin nombre"
                );
                break;
            }

            if (layer instanceof L.Path && !(layer instanceof L.CircleMarker)) {
              const point = pointOnSurface(feature);
              const center = [point.geometry.coordinates[1], point.geometry.coordinates[0]];

              const zoom = layer._map?.getZoom?.() ?? 10;

              // Ajusta el tamaño según el zoom
              const minSize = 7;   // tamaño mínimo
              const maxSize = 28;   // tamaño máximo
              let size = minSize + (zoom - 10) * 1.4;

              if (size < minSize) size = minSize;
              if (size > maxSize) size = maxSize;


              let markerColor = (layerType === "parquesWFS" && id === 3) ? "#F08DDC" : "#ff0000";
              const radius = "50%";

              const p = feature?.properties ?? {};
              let shapeHtml;

              if (activeTab === 3) {

                if (id === 0) {

                  const base = p.id_uso
                    ? COLORS_LOTES[p.id_uso] || COLORS_LOTES["Se desconoce"]
                    : COLORS_LOTES["Se desconoce"];

                  shapeHtml = makeShape(base.icon, base.fillColor, size);

                } else if (id === 2) {
                  let base = p.id_zonificacion
                    ? COLORS_ZONIFICACION[p.id_zonificacion] || COLORS_ZONIFICACION["Se desconoce"]
                    : COLORS_ZONIFICACION["Se desconoce"];

                  shapeHtml = makeShape(base.icon, base.fillColor, size);

                } else if (id === 3) {

                  let base = COLORS_ZONAS_INDUSTRIALES[1];

                  shapeHtml = makeShape(base.icon, base.fillColor, size);
                }

              } else if (activeTab === 2) {

                if (id === 4) {

                  let base = COLORS_LOTES_PARQUES["Se desconoce"] ?? {};

                  if (p.idUso && COLORS_LOTES_PARQUES[p.idUso]) {
                    base = COLORS_LOTES_PARQUES[p.idUso];
                  }
                  /*
                    if (p.zonificacion && COLORS_ZONIFICACION_PARQUES[p.zonificacion]) {
                      base = COLORS_ZONIFICACION_PARQUES[p.zonificacion];
                    } 
                    */

                  shapeHtml = makeShape(base.icon, base.fillColor, size);

                } else if (id === 3) {
                  let base = COLORS_ZONIFICACION_PARQUES["Se desconoce"] ?? {};

                  if (p.zonificacion && COLORS_ZONIFICACION_PARQUES[p.zonificacion]) {
                    base = COLORS_ZONIFICACION_PARQUES[p.zonificacion];
                  }

                  shapeHtml = makeShape(base.icon, base.fillColor, size);

                } else if (id === 2) {
                  const base = COLORS_PARQUES[6] ?? {};
                  shapeHtml = makeShape(base.icon, base.fillColor, size);

                } else if (id === 0 || id === 1) {
                  const base = COLORS_PARQUES[p.id_zonificacion] ?? {};
                  /*if(p.id_zonificacion==8){
                    base = COLORS_PARQUES[8] ?? {};
                  }*/
                  shapeHtml = makeShape(base.icon, base.fillColor, size);

                } else {
                  shapeHtml = `<div style="width:${size}px;height:${size}px;background:${markerColor};border-radius:50%;border:1px solid #fff;"></div>`;
                }
              } else {
                shapeHtml = `<div style="width:${size}px;height:${size}px;background:${markerColor};border-radius:50%;border:1px solid #fff;"></div>`;
              }


              const marker = L.marker(center, {
                icon: L.divIcon({
                  html: "", //shapeHtml
                  className: "hidden-marker",//"custom-center-marker",
                  // html: '<img src="icono.png" style="width:24px;">',
                  //className: 'polygon-icon',
                  iconSize: [size, size],
                  iconAnchor: [size / 2, size / 2],
                }),
              });
              // Tag con el pane del sublayer dueño — los markers viven en el
              // markerPane global (no en el pane de su sublayer), así que
              // `_isLayerInVisiblePane` necesita esta referencia para saber si
              // el sublayer al que pertenecen está visible. Sin esto, la
              // búsqueda abría popups de markers de sublayers ocultas (p.ej.
              // un lote industrial con su checkbox apagado).
              marker._arcgisOwnerPane = paneName;


              if (activeTab === 3) {
                if (feature.properties.id_intervencion) {
                  if (id === 2) {
                    // Summary popup (compacto) — el detalle completo abre en
                    // el modal de RightPanel vía onShowDetail.
                    marker.bindPopup(popupParqZonasIndusDetalle(feature), {
                      maxWidth: 320,
                      minWidth: 260
                    });
                  }
                }
              } else if (activeTab === 2) {
                if (feature.properties.id_intervencion) {
                  if (id === 4 && feature.properties.id_lote_infra) {
                    marker.bindPopup(popupParLotIndDetalle(feature), {
                      maxWidth: 320,
                      minWidth: 260
                    });
                  }
                  else if (id === 1) {
                    // Summary popup (compacto) — el detalle completo abre
                    // en el modal vía onShowDetail.
                    marker.bindPopup(popupParqZonasEspDetalle(feature), {
                      maxWidth: 320,
                      minWidth: 260
                    });
                  } else if (id === 2) {

                    marker.bindPopup(popupParqIndustrialesDetalle(feature), {
                      maxWidth: 320,
                      minWidth: 260
                    });
                  }

                  else if (id === 3) {
                    marker.bindPopup(popupParqZonificacionIndusDetalle(feature), {
                      maxWidth: 320,
                      minWidth: 260
                    });
                  }
                }
              }
              else if (popupTemplate) {
                let template = typeof popupTemplate === "function" ? popupTemplate(feature, id) : popupTemplate;
                marker.bindPopup(template, { maxWidth: 1200, minWidth: 380 });
              }

              marker.on("popupopen", async (e) => {

                const popupEl = e.popup.getElement();
                const id_intervencion = feature.properties.id_intervencion;
                const id_lote_infra = feature.properties.id_lote_infra;
                // const id_zonificacion = feature.properties.id_zonificacion;
                const id_zonificacion_infra = feature.properties.id_zonificacion_infra;

                if (activeTab === 2 && id_intervencion) {

                  if (id === 4 && id_lote_infra) {

                    // Summary popup — wire CTA igual que parques industriales.
                    const ctaBtn = popupEl.querySelector(
                      '[data-action="lote-ver-detalle"]'
                    );
                    if (ctaBtn && onShowDetail) {
                      ctaBtn.onclick = (ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        marker.closePopup();
                        const p = feature.properties || {};
                        const mz = p.mz ?? "-";
                        const lote = p.lote ?? "";
                        onShowDetail({
                          kind: "lote",
                          id: id_intervencion,
                          idLoteInfra: id_lote_infra,
                          nombre: lote
                            ? `Mz. ${mz} · Lote ${lote}`
                            : `Manzana ${mz}`,
                        });
                      };
                    }

                  } else if (id === 2) {

                    // Summary popup — wireamos el CTA "Ver detalle". El
                    // modal con el detalle completo lo renderiza RightPanel
                    // → DetalleParqueIndustrial cuando onShowDetail dispara.
                    const ctaBtn = popupEl.querySelector(
                      '[data-action="parque-ver-detalle"]'
                    );
                    if (ctaBtn && onShowDetail) {
                      ctaBtn.onclick = (ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        // Cerramos el popup antes de abrir el modal para
                        // que no se vean dos cosas superpuestas.
                        marker.closePopup();
                        onShowDetail({
                          kind: "parque",
                          id: id_intervencion,
                          nombre:
                            feature.properties.valor_descripcion ||
                            feature.properties.name ||
                            "Parque industrial",
                        });
                      };
                    }

                  } else if (id === 1) {

                    // Summary popup — wire CTA "Ver detalle" al modal
                    // DetalleZonaEconomicaEspecial (RightPanel).
                    const ctaBtn = popupEl.querySelector(
                      '[data-action="zed-ver-detalle"]'
                    );
                    if (ctaBtn && onShowDetail) {
                      ctaBtn.onclick = (ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        marker.closePopup();
                        onShowDetail({
                          kind: "zed",
                          id: id_intervencion,
                          nombre:
                            feature.properties.valor_descripcion ||
                            feature.properties.name ||
                            "Zona Económica Especial",
                        });
                      };
                    }

                  } else if (id === 3 && id_zonificacion_infra) {

                    // Summary popup para Zonificación — wire CTA al modal.
                    const ctaBtn = popupEl.querySelector(
                      '[data-action="zonificacion-ver-detalle"]'
                    );
                    if (ctaBtn && onShowDetail) {
                      ctaBtn.onclick = (ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        marker.closePopup();
                        const p = feature.properties || {};
                        onShowDetail({
                          kind: "zonificacion",
                          id: id_intervencion,
                          idZonificacionInfra: id_zonificacion_infra,
                          idZonificacion: p.id_zonificacion,
                          nombre: `Mz. ${p.mz || p.valor_descripcion || "-"}`,
                        });
                      };
                    }
                  }
                } else if (activeTab === 3 && id_intervencion) {
                  if (id === 2) {
                    // Summary popup — wire CTA "Ver detalle" al modal completo
                    // (DetalleZonaIndustrial). Mismo patrón que parques.
                    const ctaBtn = popupEl.querySelector(
                      '[data-action="zona-ver-detalle"]'
                    );
                    if (ctaBtn && onShowDetail) {
                      ctaBtn.onclick = (ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        marker.closePopup();
                        const p = feature.properties || {};
                        const raw =
                          p.valor_descripcion?.split(".")?.[0]?.trim() ||
                          p.valor_descripcion ||
                          p.name ||
                          "Zona industrial";
                        onShowDetail({
                          kind: "zona",
                          id: id_intervencion,
                          idZonificacionInfra: id_zonificacion_infra,
                          nombre: raw,
                        });
                      };
                    }
                  }
                }
                else {

                  const fetchedDiv = popupEl.querySelector("#fetched-data");
                  if (!fetchedDiv) return;
                  fetchedDiv.innerHTML = "<em>Cargando datos...</em>";
                  const cod = feature.properties.id_intervencion;
                  // console.log("cod:"+cod);
                  if (!cod) {
                    fetchedDiv.innerHTML = "<p>No se encontró el código de intervención.</p>";
                    return;
                  }
                  // console.log("Fetching data for code:", cod);
                  try {
                    const response = await cachedFetch(popupCache, popupInFlight, `parques|getParquesIndustriales|${cod}`, () => parquesIndustriales.getParquesIndustriales(cod), CACHE_TTL_POPUP_MS);
                    const html = Object.entries(response.items[0]).filter(([key, value]) => value !== null && value !== "-" && value !== "")
                      .map(([key, value]) => `<tr>
                                              <td style="padding:6px 10px; font-weight:bold; color:#fff; background:#ac0000; border-bottom:1px solid #ddd;">
                                                ${formatKey(key)}
                                              </td>
                                              <td style="padding:6px 10px; border-bottom:1px solid #ddd;">
                                                ${value}
                                              </td>
                                            </tr>`).join("");

                    fetchedDiv.innerHTML = `
                        <div style="max-height:300px; overflow:auto; font-family:sans-serif;">
                          <table style="border-collapse:collapse; width:100%; font-size:13px;">
                            <tbody>
                              ${html}
                            </tbody>
                          </table>
                        </div>`;
                  } catch (err) {
                    // console.error("Error fetching data:", err);
                    fetchedDiv.innerHTML = "<p>Error al cargar la información.</p>";
                  }

                }


              });

              marker.bindTooltip(layer.getTooltip().getContent());
              marker.addTo(layerGroup);
              // ===============================
              // CLICK EN EL POLÍGONO → USAR EL MISMO POPUP DEL MARKER
              // PERO REPOSICIONADO DONDE HICISTE CLIC
              // ===============================
              if (layer instanceof L.Path && !(layer instanceof L.CircleMarker)) {
                layer._centroidMarker = marker;

                layer.on("click", (e) => {
                  const m = layer._centroidMarker;
                  if (!m || !m.getPopup) return;

                  m.openPopup();

                  const popup = m.getPopup();
                  if (popup) {
                    popup.options.offset = L.point(0, 40);
                    popup.setLatLng(e.latlng);
                    //popup.update();                           

                    map.panTo(e.latlng, { animate: true });
                  }

                });
              }

            }
          },
        });
        featureLayer.addTo(layerGroup);

        // Una vez completada la carga inicial, deshabilitamos las
        // re-consultas que esri-leaflet dispara por cada zoom/pan
        // (su mecanismo tile-based de FeatureGrid). Para layers chicos
        // y medianos (todos los nuestros caben en una sweep inicial,
        // <1000 features) las features ya están todas en memoria y no
        // necesitamos pedirlas de nuevo. Esto elimina los ~7+ requests
        // por sublayer × cada cambio de zoom que el usuario reportaba.
        featureLayer.once("load", () => {
          if (typeof featureLayer._update === "function") {
            featureLayer._update = function () {};
          }
          if (typeof featureLayer._reset === "function") {
            featureLayer._reset = function () {};
          }
        });

        // Eventos para manejar carga y errores
        featureLayer.on("loading", () => {
          setLoading(true);
        });

        // Búsqueda-zoom encapsulada — se ejecuta:
        //  - desde el load handler (allowServerQuery=true: ArcGIS query como
        //    último recurso si no hay cache),
        //  - desde el effect de texto, cuando el usuario tipea sin que cambie
        //    el SQL (allowServerQuery=false: solo cache + features locales,
        //    cero red).
        const runSearchZoom = (termInput, { allowServerQuery = true } = {}) => {
          const term = (termInput || "").trim();
          if (term.length < 3) return;
          if (searchZoomDoneRef.current) return;
          // Skippeamos búsqueda en sublayers no visibles. Con pre-fetch las
          // capas de otro tab quedan montadas (panes ocultos), y antes la
          // búsqueda matcheaba en esas también → el popup de un feature
          // oculto se abría sobre el mapa sin que el polígono se viera.
          if (!visibleRef.current) return;

          const runLocalSearchZoom = () => {
            const termUpper = term.toUpperCase();
            const matchingLayers = [];

            Object.values(featureLayer._layers || {}).forEach((lyr) => {
              const props =
                lyr.feature?.properties ||
                lyr.options?.feature?.properties ||
                lyr.options?.properties ||
                {};

              const hayMatch = Object.values(props).some((val) => {
                if (typeof val !== "string") return false;
                return val.toUpperCase().includes(termUpper);
              });

              if (hayMatch) {
                matchingLayers.push(lyr);
              }
            });

            if (matchingLayers.length === 0) return;

            const fg = L.featureGroup(matchingLayers);
            const mb = fg.getBounds?.();

            if (mb && mb.isValid && mb.isValid()) {
              searchZoomDoneRef.current = true;
              // Pasamos el marker del primer match como preferido — así el
              // popup que abre es el de ESE feature concreto (p.ej. INDUPARK)
              // y no el "más cercano al centro" de los bounds, que con varios
              // matches puede caer en un área sin markers y dejar todo sin
              // popup, o peor, abrir un popup de otro feature visible.
              const firstMarker = matchingLayers[0]?._centroidMarker;
              zoomToBoundsAndOpen(map, mb, {
                maxZoom: 15,
                duration: 1.6,
                preferredLayer: firstMarker,
              });
              if (halosRef.current) halosRef.current.clearLayers();
              halosDismissedRef.current = true;
              halosBuiltRef.current = false;
            }
          };

          const termUpper = term.toUpperCase();

          try {
            const arcCacheKey = `${url}|${layerId}|${finalWhere || "1=1"}|${termUpper}`;

            // 1) bounds cache → zoom inmediato
            const cachedBounds = cacheGet(searchBoundsCache, arcCacheKey);
            if (cachedBounds?.bounds) {
              const mb = L.latLngBounds(
                [cachedBounds.bounds.sw.lat, cachedBounds.bounds.sw.lng],
                [cachedBounds.bounds.ne.lat, cachedBounds.bounds.ne.lng]
              );
              if (mb && mb.isValid && mb.isValid()) {
                searchZoomDoneRef.current = true;
                zoomToBoundsAndOpen(map, mb, { maxZoom: 15, duration: 1.2 });
                if (halosRef.current) halosRef.current.clearLayers();
                halosDismissedRef.current = true;
                halosBuiltRef.current = false;
                return;
              }
            }

            // 2) featureCollection cache → filtra en memoria y zoom
            const cachedFC = cacheGet(searchQueryCache, arcCacheKey);
            if (cachedFC?.fc && Array.isArray(cachedFC.fc.features)) {
              const matchingFeatures = cachedFC.fc.features.filter((f) => {
                const props = f?.properties || {};
                return Object.values(props).some((val) => {
                  if (typeof val !== "string") return false;
                  return val.toUpperCase().includes(termUpper);
                });
              });

              if (matchingFeatures.length > 0) {
                const fg = L.geoJSON(matchingFeatures);
                const mb = fg.getBounds?.();
                if (mb && mb.isValid && mb.isValid()) {
                  cacheSet(searchBoundsCache, arcCacheKey, {
                    bounds: {
                      sw: { lat: mb.getSouthWest().lat, lng: mb.getSouthWest().lng },
                      ne: { lat: mb.getNorthEast().lat, lng: mb.getNorthEast().lng },
                    },
                  });

                  searchZoomDoneRef.current = true;
                  zoomToBoundsAndOpen(map, mb, { maxZoom: 15, duration: 1.2 });
                  if (halosRef.current) halosRef.current.clearLayers();
                  halosDismissedRef.current = true;
                  halosBuiltRef.current = false;
                  return;
                }
              }
            }

            // 3) Si hay query en vuelo, esperar y leer bounds de cache
            if (searchInFlight.has(arcCacheKey)) {
              searchInFlight.get(arcCacheKey).then(() => {
                if (searchZoomDoneRef.current) return;
                const cb = cacheGet(searchBoundsCache, arcCacheKey);
                if (!cb?.bounds) return;
                const mb = L.latLngBounds(
                  [cb.bounds.sw.lat, cb.bounds.sw.lng],
                  [cb.bounds.ne.lat, cb.bounds.ne.lng]
                );
                if (mb && mb.isValid && mb.isValid()) {
                  searchZoomDoneRef.current = true;
                  zoomToBoundsAndOpen(map, mb, { maxZoom: 15, duration: 1.2 });
                  if (halosRef.current) halosRef.current.clearLayers();
                  halosDismissedRef.current = true;
                  halosBuiltRef.current = false;
                }
              });
              return;
            }

            // 4) Fallback: si NO se permite consulta al servidor (caso: usuario
            //    tipeando, layer ya está cargado), usar features locales.
            if (!allowServerQuery) {
              runLocalSearchZoom();
              return;
            }

            // 5) Última opción (solo durante load inicial post-filtro):
            //    query al servidor y cacheo del featureCollection.
            const _searchPromise = new Promise((resolve) => {
              featureLayer
                .query()
                .where(finalWhere || "1=1")
                .limit(2000)
                .run((err, featureCollection) => {
                  try {
                    if (err) {
                      runLocalSearchZoom();
                      return;
                    }
                    if (
                      !featureCollection ||
                      !Array.isArray(featureCollection.features) ||
                      featureCollection.features.length === 0
                    ) {
                      runLocalSearchZoom();
                      return;
                    }
                    cacheSet(
                      searchQueryCache,
                      arcCacheKey,
                      { fc: featureCollection },
                      CACHE_TTL_SEARCH_MS
                    );
                    const matchingFeatures = featureCollection.features.filter(
                      (f) => {
                        const props = f.properties || {};
                        return Object.values(props).some((val) => {
                          if (typeof val !== "string") return false;
                          return val.toUpperCase().includes(termUpper);
                        });
                      }
                    );
                    if (matchingFeatures.length === 0) {
                      runLocalSearchZoom();
                      return;
                    }
                    const fg = L.geoJSON(matchingFeatures);
                    const mb = fg.getBounds?.();
                    if (mb && mb.isValid && mb.isValid()) {
                      cacheSet(searchBoundsCache, arcCacheKey, {
                        bounds: {
                          sw: { lat: mb.getSouthWest().lat, lng: mb.getSouthWest().lng },
                          ne: { lat: mb.getNorthEast().lat, lng: mb.getNorthEast().lng },
                        },
                      });
                      searchZoomDoneRef.current = true;
                      zoomToBoundsAndOpen(map, mb, { maxZoom: 15, duration: 5 });
                      if (halosRef.current) halosRef.current.clearLayers();
                      halosDismissedRef.current = true;
                      halosBuiltRef.current = false;
                    } else {
                      runLocalSearchZoom();
                    }
                  } finally {
                    resolve();
                  }
                });
            });
            searchInFlight.set(arcCacheKey, _searchPromise);
            _searchPromise.finally(() => {
              if (searchInFlight.get(arcCacheKey) === _searchPromise) {
                searchInFlight.delete(arcCacheKey);
              }
            });
          } catch (e) {
            runLocalSearchZoom();
          }
        };

        // Registramos la función para que el effect de texto pueda invocarla
        // cuando el usuario tipea sin que cambie el SQL.
        searchZoomFnsRef.current.push(runSearchZoom);

        featureLayer.on("load", () => {
          const count = Object.keys(featureLayer._layers || {}).length;
          const term = (searchTextRef.current || "").trim();

          // 1) Sincronizamos lastSearchTermRef y reseteamos searchZoomDone si
          //    el texto cambió desde el último zoom (puede pasar cuando el
          //    layer rebuildea por cambio de filtro mientras el usuario sigue
          //    con un término de búsqueda activo).
          if (term !== lastSearchTermRef.current) {
            lastSearchTermRef.current = term;
            searchZoomDoneRef.current = false;
          }

          // 2) Zoom por búsqueda — permitido query al server como fallback,
          //    porque estamos en el load inicial post-filtro.
          runSearchZoom(term, { allowServerQuery: true });

          // 3) Marca didZoom local para el bloque de zoom-por-filtro.
          let didZoomLocal = searchZoomDoneRef.current;

          // ==========================
          // 4) BOUNDS PARA ZOOM POR FILTRO
          // ==========================
          const b = getSafeBounds(featureLayer, layerGroup);

          if (!firstValidBounds && count > 0 && b && finalWhere !== "1=1") {
            firstValidBounds = b;
          }

          // Terminó de cargar una capa
          pendingLoads -= 1;

          // ==========================
          // 5) CUANDO TERMINAN TODAS LAS CAPAS
          // ==========================
          if (pendingLoads === 0) {
            // 5.1) ZOOM POR FILTRO (si no hubo zoom por búsqueda)
            if (
              !didZoomLocal &&            // no hubo zoom anterior
              term.length < 3 &&          // no hay búsqueda activa
              !filterZoomDoneRef.current && // aún no se hizo zoom por filtro
              finalWhere !== "1=1" &&
              firstValidBounds
            ) {
              didZoomLocal = true;
              filterZoomDoneRef.current = true;



              zoomToBoundsAndOpen(map, firstValidBounds, {
                maxZoom: 13,
                duration: 1.6,
              });


              if (halosRef.current) {
                halosRef.current.clearLayers();
              }
              halosDismissedRef.current = true;
              halosBuiltRef.current = false;
            }
            /*
                if (
                activeTab === 3 &&               // SOLO TAB 3
                !didZoom &&                      // No hubo zoom de carga
                term.length < 3 &&               // No búsqueda
                finalWhere === "1=1" &&          // Sin filtros
                !zonasZoomDoneRef.current &&     // Solo una vez
                map
              ) {
                zonasZoomDoneRef.current = true;
    
                map.flyTo([-12.0464, -77.0428], 12, {
                  duration: 2.1,
                  easeLinearity: 0.25
                });
              }
            */

            const buildHalos = () => {
              if (!map || !halosRef.current) return;

              // si el usuario ya los quitó o ya están construidos, no hacer nada
              if (halosDismissedRef.current) return;
              if (halosBuiltRef.current) return;

              // sólo tabs 2 y 3 usan halos
              if (activeTab !== 2 && activeTab !== 3) return;

              const currentZoom = map.getZoom ? map.getZoom() : null;

              // si ya estoy muy cerca, no dibujar halos
              if (currentZoom !== null && currentZoom > HALO_MAX_ZOOM_TO_SHOW) {
                halosDismissedRef.current = true;
                halosBuiltRef.current = false;
                return;
              }

              halosRef.current.clearLayers();
              let anyHaloCreated = false;

              // =====================================================
              //  RAMA 1: TAB 2  → lógica nueva (halo por polígono 0,1,2 + globos)
              // =====================================================
              if (activeTab === 2) {
                const haloGroups = {};

                newLayers.forEach((lg) => {
                  const layerId = lg._arcgisLayerId ?? -1;
                  const srcLayers = lg.getLayers
                    ? lg.getLayers()
                    : Object.values(lg._layers || {});

                  srcLayers.forEach((lyr) => {
                    let center = null;

                    // Preferimos bounds (polígonos)
                    if (lyr.getBounds && lyr.feature) {
                      try {
                        const bb = lyr.getBounds();
                        if (bb && bb.isValid && bb.isValid()) {
                          center = bb.getCenter();
                        }
                      } catch (e) { }
                    }

                    // Si no hay bounds, intentamos latLng (puntos)
                    if (!center && lyr.getLatLng) {
                      try {
                        center = lyr.getLatLng();
                      } catch (e) { }
                    }

                    if (
                      !center ||
                      typeof center.lat !== "number" ||
                      typeof center.lng !== "number" ||
                      Number.isNaN(center.lat) ||
                      Number.isNaN(center.lng)
                    ) {
                      return;
                    }

                    // ---------- 1) Capas 0,1,2 → halo POR POLÍGONO ----------
                    if (layerId === 0 || layerId === 1 || layerId === 2) {
                      const cfg = getHaloConfigByLayerId(layerId);

                      const isMainLayer = true;
                      const radius = isMainLayer ? 1000 : HALO_RADIUS_METERS;

                      const halo = L.circle(center, {
                        pane: "halo-pane",
                        radius,
                        color: cfg.fillColor,
                        weight: 2,
                        opacity: 0.9,
                        fillColor: cfg.fillColor,
                        fillOpacity: 0.3,
                        interactive: true,
                      });

                      const handleClickPolygon = () => {
                        if (!map) return;

                        halosDismissedRef.current = true;
                        halosBuiltRef.current = false;
                        if (halosRef.current) {
                          halosRef.current.clearLayers();
                        }

                        let bounds = null;

                        if (lyr.getBounds) {
                          try {
                            const b = lyr.getBounds();
                            if (b && b.isValid && b.isValid()) {
                              bounds = b;
                            }
                          } catch (e) { }
                        }

                        if (!bounds && lyr.getLatLng) {
                          try {
                            const ll = lyr.getLatLng();
                            bounds = L.latLngBounds(ll, ll);
                          } catch (e) { }
                        }

                        if (bounds && bounds.isValid && bounds.isValid()) {
                          if (map.flyToBounds) {
                            map.flyToBounds(bounds, {
                              maxZoom: 15,
                              duration: 1.5,
                              easeLinearity: 0.2,
                              padding: [40, 40],
                            });
                          } else {
                            map.fitBounds(bounds, { maxZoom: 15, padding: [40, 40] });
                          }
                        }
                      };

                      halo.on("click", handleClickPolygon);
                      halo.addTo(halosRef.current);
                      if (halo.bringToFront) halo.bringToFront();

                      // Icono tipo globo
                      const iconCfg = getHaloConfigByLayerId(layerId);
                      const bg = iconCfg.bgColor || iconCfg.fillColor || "#1976d2";

                      const iconMarker = L.marker(center, {
                        pane: "halo-pane",
                        icon: L.divIcon({
                          html: `
                            <div class="halo-pin" style="background:${bg}; color:${bg};">
                              <img src="${iconCfg.icon}" />
                            </div>
                          `,
                          className: "halo-pin-wrapper",
                          iconSize: [34, 42],
                          iconAnchor: [17, 42],
                        }),
                      });

                      iconMarker.on("click", handleClickPolygon);
                      iconMarker.addTo(halosRef.current);
                      anyHaloCreated = true;
                      return; // no pasar a agrupamiento
                    }

                    // ---------- 2) Resto de capas en TAB 2 , TAB 3 → agrupación por grilla + globo ----------
                    const lat = center.lat;
                    const lng = center.lng;

                    const gx = Math.floor(lng / GRID_SIZE_DEGREES);
                    const gy = Math.floor(lat / GRID_SIZE_DEGREES);
                    const key = `${gx}_${gy}`;

                    if (!haloGroups[key]) {
                      haloGroups[key] = {
                        sumLat: 0,
                        sumLng: 0,
                        count: 0,
                        layers: [],
                        layerId,
                      };
                    }

                    const g = haloGroups[key];
                    g.sumLat += lat;
                    g.sumLng += lng;
                    g.count += 1;
                    g.layers.push(lyr);
                  });
                });

                const groupsArray = Object.values(haloGroups);
                groupsArray.forEach((g) => {
                  const center = [g.sumLat / g.count, g.sumLng / g.count];
                  const cfg = getHaloConfigByLayerId(g.layerId);

                  const halo = L.circle(center, {
                    pane: "halo-pane",
                    radius: HALO_RADIUS_METERS,
                    color: cfg.fillColor,
                    weight: 2,
                    opacity: 0.9,
                    fillColor: cfg.fillColor,
                    fillOpacity: 0.3,
                    interactive: true,
                  });

                  const handleClickGroup = () => {
                    if (!map) return;

                    halosDismissedRef.current = true;
                    halosBuiltRef.current = false;
                    if (halosRef.current) {
                      halosRef.current.clearLayers();
                    }

                    const fg = L.featureGroup(g.layers);
                    const bounds = fg.getBounds?.();

                    if (bounds && bounds.isValid && bounds.isValid()) {
                      if (map.flyToBounds) {
                        map.flyToBounds(bounds, {
                          maxZoom: 15,
                          duration: 1.5,
                          easeLinearity: 0.2,
                          padding: [40, 40],
                        });
                      } else {
                        map.fitBounds(bounds, { maxZoom: 15, padding: [40, 40] });
                      }
                    }
                  };

                  halo.on("click", handleClickGroup);
                  halo.addTo(halosRef.current);
                  if (halo.bringToFront) halo.bringToFront();

                  const bg = cfg.bgColor || cfg.fillColor || "#1976d2";

                  const iconMarker = L.marker(center, {
                    pane: "halo-pane",
                    icon: L.divIcon({
                      html: `
                        <div class="halo-pin" style="background:${bg}; color:${bg};">
                          <img src="${cfg.icon}" />
                        </div>
                      `,
                      className: "halo-pin-wrapper",
                      iconSize: [34, 40],
                      iconAnchor: [17, 40],
                    }),
                  });

                  iconMarker.on("click", handleClickGroup);
                  iconMarker.addTo(halosRef.current);
                  anyHaloCreated = true;
                });
              }
              // =====================================================
              //  RAMA 2: OTROS TABS (ej. TAB 3) → COMPORTAMIENTO ORIGINAL
              //  (agrupamiento por grilla + icono redondo .halo-icon)
              // =====================================================
              /* else {
                  const haloGroups = {};
  
                  newLayers.forEach((lg) => {
                    const layerId = lg._arcgisLayerId ?? -1;
                    const srcLayers = lg.getLayers
                      ? lg.getLayers()
                      : Object.values(lg._layers || {});
  
                    srcLayers.forEach((lyr) => {
                      let center = null;
  
                      if (lyr.getBounds && lyr.feature) {
                        try {
                          const bb = lyr.getBounds();
                          if (bb && bb.isValid && bb.isValid()) {
                            center = bb.getCenter();
                          }
                        } catch (e) {}
                      }
  
                      if (!center && lyr.getLatLng) {
                        try {
                          center = lyr.getLatLng();
                        } catch (e) {}
                      }
  
                      if (
                        !center ||
                        typeof center.lat !== "number" ||
                        typeof center.lng !== "number" ||
                        Number.isNaN(center.lat) ||
                        Number.isNaN(center.lng)
                      ) {
                        return;
                      }
  
                      const lat = center.lat;
                      const lng = center.lng;
  
                      const gx = Math.floor(lng / GRID_SIZE_DEGREES);
                      const gy = Math.floor(lat / GRID_SIZE_DEGREES);
                      const key = `${gx}_${gy}`;
  
                      if (!haloGroups[key]) {
                        haloGroups[key] = {
                          sumLat: 0,
                          sumLng: 0,
                          count: 0,
                          layers: [],
                          layerId,
                        };
                      }
  
                      const g = haloGroups[key];
                      g.sumLat += lat;
                      g.sumLng += lng;
                      g.count += 1;
                      g.layers.push(lyr);
                    });
                  });
  
                  const groupsArray = Object.values(haloGroups);
                  groupsArray.forEach((g) => {
                    const center = [g.sumLat / g.count, g.sumLng / g.count];
                    const cfg = getHaloConfigByLayerId(g.layerId);
  
                    const halo = L.circle(center, {
                      pane: "halo-pane",
                      radius: 28000,
                      color: cfg.fillColor,
                      weight: 0,
                      fillColor: cfg.fillColor,
                      fillOpacity: 1,
                      interactive: true,
                    });
  
                    halo.on("click", () => {
                      if (!map) return;
  
                      halosDismissedRef.current = true;
                      halosBuiltRef.current = false;
                      if (halosRef.current) {
                        halosRef.current.clearLayers();
                      }
  
                      const fg = L.featureGroup(g.layers);
                      const bounds = fg.getBounds?.();
  
                      if (bounds && bounds.isValid && bounds.isValid()) {
                        if (map.flyToBounds) {
                          map.flyToBounds(bounds, {
                            maxZoom: 15,
                            duration: 1.5,
                            easeLinearity: 0.2,
                            padding: [40, 40],
                          });
                        } else {
                          map.fitBounds(bounds, { maxZoom: 15, padding: [40, 40] });
                        }
                      }
                    });
  
                    halo.addTo(halosRef.current);
                    if (halo.bringToFront) halo.bringToFront();
  
              
                    const iconMarker = L.marker(center, {
                      pane: "halo-pane",
                      interactive: false,
                      icon: L.divIcon({
                        html: `
                          <div class="halo-icon">
                            <img src="${cfg.icon}" style="width:20px;height:20px;" />
                          </div>
                        `,
                        className: "halo-icon-wrapper",
                        iconSize: [24, 24],
                        iconAnchor: [12, 12],
                      }),
                    });
  
                    iconMarker.addTo(halosRef.current);
                    anyHaloCreated = true;
                  });
                }*/

              // marcar que se construyeron halos y guardar zoom
              if (anyHaloCreated) {
                halosBuiltRef.current = true;
                if (!initialZoomRef.current && map) {
                  initialZoomRef.current = map.getZoom();
                }
              }
            };

            buildHalos();

            if (!zoomHandlerRef.current && map) {
              const handler = () => {
                if (!map) return;
                const currentZoom = map.getZoom();

                // si aún no tenemos el zoom inicial, lo tomamos
                if (initialZoomRef.current === null) {
                  initialZoomRef.current = currentZoom;
                }

                // 1) ACERCAMIENTO: si me acerco bastante desde el zoom inicial → oculto halos
                if (
                  halosBuiltRef.current &&
                  !halosDismissedRef.current &&
                  currentZoom >= initialZoomRef.current + (activeTab === 2 ? HALO_DELTA_IN_HIDE : 2)
                ) {
                  halosDismissedRef.current = true;
                  halosBuiltRef.current = false;
                  halosRef.current?.clearLayers();
                  return;
                }

                // 2) ALEJAMIENTO: si vuelvo a un zoom cercano al inicial (y no muy cerca) → reaparecen halos
                if (
                  halosDismissedRef.current &&
                  currentZoom <= initialZoomRef.current + (activeTab === 2 ? HALO_DELTA_OUT_SHOW : 1.6) &&
                  currentZoom <= HALO_MAX_ZOOM_TO_SHOW
                ) {
                  halosDismissedRef.current = false;
                  halosBuiltRef.current = false;
                  buildHalos();
                }

              };

              zoomHandlerRef.current = handler;
              map.on("zoomend", handler);



            }


            // 3.4) Fin de carga
            setLoading(false);

            if (onLayerLoad) {
              onLayerLoad({
                layerId: id,
                count,
              });
            }
          }
        });

        featureLayer.on("error", (err) => {
          setLoading(false);
          setError(
            `Error en capa ${id}: ${err.message || "Error al cargar la capa"}`
          );
          if (onLayerError) {
            onLayerError(err, id);
          }
        });

        newLayers.push(layerGroup);
      });


      // Guardamos los layerGroups creados para que el efecto pequeño de
      // visibilidad pueda toggle add/remove sin re-construirlos.
      layerGroupsRef.current = newLayers;

      return () => {
        newLayers.forEach((layerGroup) => {
          if (layerGroup && map.hasLayer(layerGroup)) {
            map.removeLayer(layerGroup);
          }
          // También removemos el pane que creamos para esta sublayer.
          // Si el cleanup es por cambio de deps (url/where), el rebuild
          // re-crea el pane con el mismo nombre. Si es por unmount, dejarlo
          // sería un leak menor en el DOM del map.
          const paneName = layerGroup?._arcgisPaneName;
          if (paneName && map._panes && map._panes[paneName]) {
            const pane = map._panes[paneName];
            if (pane && typeof pane.remove === "function") pane.remove();
            delete map._panes[paneName];
          }
        });
        layerGroupsRef.current = [];

        if (zoomHandlerRef.current && map) {
          map.off("zoomend", zoomHandlerRef.current);
          zoomHandlerRef.current = null;
        }

      };
    } catch (err) {
      setLoading(false);
      setError(err.message || "Error al crear las capas");
      if (onLayerError) {
        onLayerError(err);
      }
    }
  }, [
    map,
    url,
    layerId,
    layerType,
    style,
    popupTemplate,
    onLayerLoad,
    onLayerError,
    onShowDetail,
    // Dep es el SQL string (estable), NO el objeto `where`. Así, cuando solo
    // cambia `__text`, este effect NO re-corre y no hay re-fetch al server.
    sqlWhere,
    activeTab
  ]);

  // Toggle de visibilidad — solo manipula pane.style.display. El layerGroup
  // ya está en el mapa (pre-fetch desde el mount), así esri-leaflet no
  // re-arranca su tile loading al re-mostrar. Cero red al togglear.
  useEffect(() => {
    if (!map) return;
    const groups = layerGroupsRef.current || [];
    groups.forEach((g) => {
      if (!g) return;
      const paneName = g._arcgisPaneName;
      const pane = paneName ? map.getPane(paneName) : null;
      if (pane) pane.style.display = visible ? "" : "none";
    });
    // Halos viven en otro layerGroup y son markers locales (no tile-based),
    // así que para ellos sí es seguro add/remove sin penalizar red.
    if (halosRef.current) {
      if (visible) {
        if (!map.hasLayer(halosRef.current)) halosRef.current.addTo(map);
      } else {
        if (map.hasLayer(halosRef.current)) map.removeLayer(halosRef.current);
      }
    }
  }, [visible, map]);

  // Búsqueda por texto sin rebuild — corre cuando solo cambia `searchTextRaw`
  // (el usuario tipea en el buscador). Llama a runSearchZoom de cada sublayer
  // ya construido con allowServerQuery=false: la búsqueda se resuelve contra
  // cache + features locales (cero API calls). Esto es clave: cada keystroke
  // antes invalidaba el cache porque el `where` cambiaba de referencia y el
  // effect grande rebuildaba las capas; ahora el SQL es el dep, el rebuild
  // no ocurre y aquí solo zoomeamos al match.
  useEffect(() => {
    if (!map) return;
    const term = (searchTextRaw || "").trim();
    if (term !== lastSearchTermRef.current) {
      lastSearchTermRef.current = term;
      searchZoomDoneRef.current = false;
    }
    if (term.length < 3) return;
    const fns = searchZoomFnsRef.current || [];
    fns.forEach((fn) => {
      try {
        fn(term, { allowServerQuery: false });
      } catch (e) {
        // no romper otros sublayers si uno falla
      }
    });
  }, [searchTextRaw, map]);

  // Eliminado el badge "Cargando capa(s) ArcGIS..." que aparecía en la
  // esquina superior derecha. Con pre-fetch los 8 sublayers cargan al
  // inicio y cada una mostraba/ocultaba su propio badge → blink. El
  // mapa ya da feedback visual (las features aparecen al cargar), no
  // hay necesidad de un indicador separado.
  return null;
};

ArcGISFeatureLayer.defaultProps = {
  style: { color: "#3388ff", weight: 2 },
  popupTemplate: (feature, layerId) => {
    const props = feature.properties || {};
    return `
      <div style="max-width: 1200px;">
        <h4 style="margin: 0 0 8px 0; color: #333;">${props.NOMBRE || "Sin nombre"
      }</h4>
        <hr style="margin: 8px 0;">
        ${props.OBJECTID
        ? `<p style="margin: 4px 0;"><strong>ID:</strong> ${props.OBJECTID}</p>`
        : ""
      }
        ${props.DESCRIPCION
        ? `<p style="margin: 4px 0;"><strong>Descripción:</strong> ${props.DESCRIPCION}</p>`
        : ""
      }
        ${props.UBICACION
        ? `<p style="margin: 4px 0;"><strong>Ubicación:</strong> ${props.UBICACION}</p>`
        : ""
      }
        ${props.AREA
        ? `<p style="margin: 4px 0;"><strong>Área:</strong> ${props.AREA} ha</p>`
        : ""
      }
        ${layerId !== undefined
        ? `<p style="margin: 4px 0;"><strong>Capa:</strong> ${layerId}</p>`
        : ""
      }
      </div>
    `;
  },
};

const capitalizeWords = (txt) => {
  if (!txt) return "-";

  return txt
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/*
async function loadParqueZonasIndustriaDetalle(popupEl, id_intervencion, id_zonificacion_infra) {
  const msgCont = popupEl.querySelector("#msjParqZonasIndusDet");
  const cont = popupEl.querySelector("#data-parq-zonas-indus");

  if (msgCont) msgCont.innerHTML = "<em>Cargando información...</em>";
  if (!cont) return;

  try {
    // ============================
    // 1) Cargar detalle (SERVICIO 1)
    // ============================
    const response = await cachedFetch(
      popupCache,
      popupInFlight,
      `parques|getIntervencionParamDetalle|zoninf:${id_zonificacion_infra}`,
      () => parquesIndustriales.getIntervencionParamDetalle(id_intervencion, id_zonificacion_infra),
      CACHE_TTL_POPUP_MS
    );

    const item = response?.item;
    if (!item) {
      if (msgCont) msgCont.innerHTML = "<p>No se encontraron datos.</p>";
      return;
    }

    const params = item.lista_param || [];
    const getParam = (nombre) => {
      const row = params.find((x) => (x.infraestructura || "").toLowerCase() === nombre.toLowerCase());
      if (!row) return "-";
      let val = (row.valor_descripcion || "").trim();
      if (!val) val = "-";
      if (nombre.toLowerCase() === "area" && val !== "-") val = `${val} ha`;
      return val;
    };

    let nombre = capitalizeWords(getParam("Nombre"));
    let glosa  = getParam("Glosa");
    let area   = getParam("Area");
    let ambito = capitalizeWords(getParam("Ambito"));

    let nombreFinal = nombre;
    if (glosa && glosa !== "-" && glosa.trim().toLowerCase() !== nombre.trim().toLowerCase()) {
      nombreFinal = `${nombre} - ${glosa}`;
    }

    // ============================
    // 2) Render inicial (sin imágenes aún)
    // ============================
    cont.innerHTML = `
      <div class="popup-scroll">
        <table style="width:100%;border-collapse:collapse;font-size:13px;table-layout:fixed;">
          <tbody>
            <tr>
              <td rowspan="3" style="width:150px; vertical-align:top; padding-right:10px;">
                
                <div id="zonind-carousel-wrap" style="display:none;">
                  <div class="carousel-container">
                    <img 
                      id="zonind-carousel-main-img"
                      src=""
                      alt="Imagen de zona industrial"
                      class="carousel-img"
                    />
                    <div class="carousel-controls">
                      <button class="carousel-btn" id="zonind-carousel-prev">‹</button>
                      <button class="carousel-btn" id="zonind-carousel-next">›</button>
                    </div>
                  </div>
                </div>

                <span id="zonind-foto-placeholder"
                  style="font-size:12px;color:#777;display:inline-block;margin-top:4px;">
                  (Cargando fotos…)
                </span>
              </td>

              <td class="popup-td-label">Zona Industrial</td>
              <td class="popup-td-value" colspan="2">${nombreFinal}</td>
            </tr>

            <tr>
              <td class="popup-td-label">Ámbito</td>
              <td class="popup-td-value" colspan="2">${ambito}</td>
            </tr>

            <tr>
              <td class="popup-td-label">Área</td>
              <td class="popup-td-value" colspan="2">${area}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    if (msgCont) msgCont.innerHTML = "";

    // ============================
    // 3) Cargar imágenes (SERVICIO 2) - aparte
    // ============================
    const imagesResp = await cachedFetch(
      popupCache,
      popupInFlight,
      `parques|getZonificacionInfraImagenes|zoninf:${id_zonificacion_infra}`,
 
      () => parquesIndustriales.getZonificacionInfraImagenes(id_zonificacion_infra),
      CACHE_TTL_POPUP_MS
    );

    // Normaliza cualquier forma que venga del servicio
    // Ejemplos soportados:
    // - { items: ["url1","url2"] }
    // - { items: [{url:"..."},{url:"..."}] }
    // - { lista: [...] } etc.
    const images = (() => {
      const arr =
        imagesResp?.items ||
        imagesResp?.lista ||
        imagesResp?.imagenes ||
        imagesResp?.data ||
        [];

      if (!Array.isArray(arr)) return [];

      return arr
        .map(x => (typeof x === "string" ? x : (x?.url || x?.src || x?.path || "")))
        .map(s => (s || "").trim())
        .filter(Boolean);
    })();

    // ============================
    // 4) Activar carrusel si llegaron imágenes
    // ============================
    const wrap   = popupEl.querySelector("#zonind-carousel-wrap");
    const mainImg= popupEl.querySelector("#zonind-carousel-main-img");
    const btnPrev= popupEl.querySelector("#zonind-carousel-prev");
    const btnNext= popupEl.querySelector("#zonind-carousel-next");
    const ph     = popupEl.querySelector("#zonind-foto-placeholder");

    if (images.length > 0 && wrap && mainImg && btnPrev && btnNext) {
      let index = 0;

      wrap.style.display = "block";
      if (ph) ph.style.display = "none";

      mainImg.src = images[0];
      mainImg.style.cursor = "zoom-in";
      mainImg.onclick = () => window.zoomImage && window.zoomImage(mainImg.src);

      if (images.length === 1) {
        btnPrev.style.display = "none";
        btnNext.style.display = "none";
      } else {
        btnPrev.style.display = "";
        btnNext.style.display = "";
      }

      btnPrev.onclick = () => {
        index = (index - 1 + images.length) % images.length;
        mainImg.src = images[index];
      };
      btnNext.onclick = () => {
        index = (index + 1) % images.length;
        mainImg.src = images[index];
      };
    } else {
      // no llegaron imágenes
      if (wrap) wrap.style.display = "none";
      if (ph) {
        ph.style.display = "inline-block";
        ph.textContent = "(Foto no disponible)";
      }
    }

  } catch (err) {
    console.error("Error en loadParqueZonasIndustriaDetalle:", err);
    if (msgCont) msgCont.innerHTML = "<p>Error al cargar la información.</p>";
  }
}
*/
async function loadParqueZonasIndustriaDetalle(popupEl, id_intervencion, id_zonificacion_infra) {
  const msgCont = popupEl.querySelector("#msjParqZonasIndusDet");
  const cont = popupEl.querySelector("#data-parq-zonas-indus");

  if (msgCont) msgCont.innerHTML = "<em>Cargando información...</em>";
  if (!cont) return;

  // ============================
  // Helper: leer parámetro por nombre
  // ============================
  const getParamValue = (params, nombre) => {
    const row = (params || []).find(
      (x) => (x.infraestructura || "").toLowerCase() === String(nombre || "").toLowerCase()
    );
    if (!row) return "-";

    let val = (row.valor_descripcion || "").trim();
    if (!val) val = "-";

    if (String(nombre).toLowerCase() === "area" && val !== "-") {
      val = `${val} ha`;
    }
    return val;
  };

  // ============================
  // Helper: galería con miniaturas
  // ============================
  const renderThumbGallery = (popupEl2, images) => {
    const mainImg = popupEl2.querySelector("#zonind-main-img");
    const empty = popupEl2.querySelector("#zonind-empty");
    const thumbs = popupEl2.querySelector("#zonind-thumbs");

    if (!mainImg || !empty || !thumbs) return;

    if (!images || images.length === 0) {
      mainImg.style.display = "none";
      thumbs.style.display = "none";
      empty.style.display = "block";
      empty.textContent = "(Foto no disponible)";
      return;
    }

    // principal
    mainImg.src = images[0];
    mainImg.style.display = "block";
    empty.style.display = "none";

    // zoom opcional
    mainImg.style.cursor = "zoom-in";
    mainImg.onclick = () => window.zoomImage && window.zoomImage(mainImg.src);

    // si solo hay 1 imagen, no miniaturas
    if (images.length === 1) {
      thumbs.style.display = "none";
      return;
    }

    // thumbs
    thumbs.innerHTML = images
      .map(
        (u, i) => `
          <img
            src="${u}"
            class="zonind-thumb ${i === 0 ? "active" : ""}"
            data-idx="${i}"
            alt=""
          />
        `
      )
      .join("");

    thumbs.style.display = "flex";

    // click en thumb (delegación)
    thumbs.onclick = (e) => {
      const el = e.target;
      if (!el || !el.classList || !el.classList.contains("zonind-thumb")) return;

      const idx = Number(el.dataset.idx);
      if (Number.isNaN(idx)) return;

      mainImg.src = images[idx];

      thumbs.querySelectorAll(".zonind-thumb").forEach((t) => t.classList.remove("active"));
      el.classList.add("active");
    };
  };

  try {
    // ============================
    // 1) Servicio DETALLE (ya lo tienes)
    // ============================
    const response = await cachedFetch(
      popupCache,
      popupInFlight,
      `parques|getIntervencionParamDetalle|${id_intervencion}|${id_zonificacion_infra}`,
      () => parquesIndustriales.getIntervencionParamDetalle(id_intervencion, id_zonificacion_infra),
      CACHE_TTL_POPUP_MS
    );

    const item = response?.item;

    if (!item) {
      if (msgCont) msgCont.innerHTML = "<p>No se encontraron datos.</p>";
      return;
    }

    const params = item.lista_param || [];

    // ============================
    // 2) Campos UI
    // ============================
    let nombre = getParamValue(params, "Nombre");
    let glosa = getParamValue(params, "Glosa");
    let area = getParamValue(params, "Area");
    let ambito = getParamValue(params, "Ambito");
    let estado = getParamValue(params, "Estado");

    nombre = capitalizeWords(nombre);
    ambito = capitalizeWords(ambito);

    const nomProjRaw = item.nombre_proyecto || item.zonificacion_infra || "Parque / Zona Industrial";
    const nomProj = capitalizeWords(nomProjRaw);

    let nombreFinal = nombre;
    if (glosa && glosa !== "-" && glosa.trim().toLowerCase() !== nombre.trim().toLowerCase()) {
      nombreFinal = `${nombre} - ${glosa}`;
    }

    // ============================
    // 3) Render inicial (placeholder imágenes)
    // ============================
    cont.innerHTML = `
      <div class="popup-scroll">
        <table style="width:100%;border-collapse:collapse;font-size:13px;table-layout:fixed;">
          <tbody>
            <tr>
              <!-- IMAGEN / MINIATURAS -->
              <td rowspan="5" style="width:170px; vertical-align:top; padding-right:10px;">
                <div class="zonind-photo">
                  <img id="zonind-main-img" class="zonind-main-img" src="" alt="Imagen" style="display:none;" />
                  <div id="zonind-empty" class="zonind-empty">(Cargando imágenes…)</div>
                  <div id="zonind-thumbs" class="zonind-thumbs" style="display:none;"></div>
                </div>
              </td>

              <td class="popup-td-label">Zona Industrial</td>
              <td class="popup-td-value" colspan="2">${nombreFinal}</td>
            </tr>

            <tr>
              <td class="popup-td-label">Ámbito</td>
              <td class="popup-td-value" colspan="2">${ambito}</td>
            </tr>

            <tr>
              <td class="popup-td-label">Área</td>
              <td class="popup-td-value" colspan="2">${area}</td>
            </tr>

            <tr>
              <td class="popup-td-label">Proyecto</td>
              <td class="popup-td-value" colspan="2">${nomProj}</td>
            </tr>
            <tr> 
              <td class="popup-td-label">Estado</td>
              <td class="popup-td-value" colspan="2">${estado}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    if (msgCont) msgCont.innerHTML = "";

    // ============================
    // 4) Servicio IMÁGENES (aparte)
    // Endpoint: /api/admin/zonificacion/{id}/imagenes
    // ============================
    const imgsResp = await cachedFetch(
      popupCache,
      popupInFlight,
      `admin|zonificacion|imagenes|${id_zonificacion_infra}`,
      () => parquesIndustriales.getZonificacionInfraImagenes(id_zonificacion_infra),
      CACHE_TTL_POPUP_MS
    );

    // tu backend devuelve { hasError, items:[{url,...}] }
    if (imgsResp?.hasError) {
      renderThumbGallery(popupEl, []);
      return;
    }

    const images = (imgsResp?.items || [])
      .map(x => (x?.url || "").trim())
      .filter(Boolean)
      .map(u => {
        if (u.startsWith("http")) return u;
        return `${window.location.origin}${u}`;
      });


    // ============================
    // 5) Pintar galería
    // ============================
    renderThumbGallery(popupEl, images);

  } catch (err) {
    console.error("Error en loadParqueZonasIndustriaDetalle:", err);
    if (msgCont) msgCont.innerHTML = "<p>Error al cargar la información.</p>";
  }
}

async function loadParqueZonificacionIndustriaDetalle(
  popupEl,
  id_intervencion,
  id_zonificacion_infra
) {
  const msgCont = popupEl.querySelector("#msjParqZonificacionIndusDet");
  const cont = popupEl.querySelector("#data-parque-zonificacion-detalle");

  if (msgCont) {
    msgCont.innerHTML = "<em>Cargando información...</em>";
  }
  if (!cont) return;

  // IDs de infraestructura que quieres mostrar
  const INFRA_IDS = {
    MZ: 11,
    ETAPA: 14,
    AREA: 21,
    ZONIFICACION: 29,
    LOTES_DISPONIBLES: 53,
    TOTAL_LOTES: 55,
    VENDIDOS: 58,
    RESERVADOS: 59
  };

  const getParamById = (lista, idInfra, defaultValue = "-") => {
    if (!Array.isArray(lista)) return defaultValue;
    const row = lista.find(p => p.id_infraestructura === idInfra);
    if (!row) return defaultValue;

    const val = (row.valor_descripcion ?? "").toString().trim();
    return val === "" ? defaultValue : val;
  };

  try {
    const response = await cachedFetch(popupCache, popupInFlight, `parques|getIntervencionParamDetalle|${id_intervencion,
      id_zonificacion_infra,
      3
      }`, () => parquesIndustriales.getIntervencionParamDetalle(
        id_intervencion,
        id_zonificacion_infra,
        3
      ), CACHE_TTL_POPUP_MS);

    // Soportar distintas formas de respuesta: array, { items }, { item }, etc.
    let row = null;

    if (Array.isArray(response)) {
      row = response[0] || null;
    } else if (Array.isArray(response?.items)) {
      row = response.items[0] || null;
    } else if (response?.item) {
      row = response.item;
    } else {
      row = response || null;
    }

    if (!row) {
      if (msgCont) {
        msgCont.innerHTML = "<p>No se encontraron datos de zonificación.</p>";
      }
      return;
    }

    const lista = Array.isArray(row.lista_param) ? row.lista_param : [];

    // === Obtener cada valor directamente de lista_param (sin cálculos) ===
    let mz = getParamById(lista, INFRA_IDS.MZ);
    let etapa = getParamById(lista, INFRA_IDS.ETAPA);
    let zoni = getParamById(lista, INFRA_IDS.ZONIFICACION, row.zonificacion || "-");
    let area = getParamById(lista, INFRA_IDS.AREA);
    let totalLotes = getParamById(lista, INFRA_IDS.TOTAL_LOTES);
    let disponibles = getParamById(lista, INFRA_IDS.LOTES_DISPONIBLES);
    let vendidos = getParamById(lista, INFRA_IDS.VENDIDOS);
    let reservados = getParamById(lista, INFRA_IDS.RESERVADOS);

    // Opcional: capitalizar algunos textos
    mz = mz === "-" ? "-" : capitalizeWords(mz);
    //etapa = etapa === "-" ? "-" : capitalizeWords(etapa);
    zoni = zoni === "-" ? "-" : zoni;

    // Header Mz si existe
    const mzHeaderEl = popupEl.querySelector("#res2-mz-header");
    if (mzHeaderEl) mzHeaderEl.textContent = "Mz." + mz || "-";

    // Contenido principal: mostramos TODOS los campos mencionados
    cont.innerHTML = `
      <div class="popup-scroll">
        <table class="popup-table">
          <tbody>

            <tr>
              <td class="popup-td-label">Etapa</td>
              <td class="popup-td-value">${etapa}</td>
              <td class="popup-td-label">Total de lotes</td>
              <td class="popup-td-value-right">${totalLotes}</td>
            </tr>

            <tr>
              <td class="popup-td-label">Mz</td>
              <td class="popup-td-value-right">${mz}</td> 
              <td class="popup-td-label">Lotes vendidos</td>
              <td class="popup-td-value-right">${vendidos}</td> 
            </tr>

            <tr>
              <td class="popup-td-label">Área</td>
              <td class="popup-td-value">${area}</td>
              <td class="popup-td-label">Lotes reservados</td>
              <td class="popup-td-value">${reservados}</td>              
            </tr>

            <tr>
              <td class="popup-td-label">Zonificación</td>
              <td class="popup-td-value-right">${zoni}</td>
              <td class="popup-td-label">Lotes disponibles</td>
              <td class="popup-td-value">${disponibles}</td>              
            </tr>

          </tbody>
        </table>
      </div>
    `;

    if (msgCont) {
      msgCont.innerHTML = "";
    }

  } catch (err) {
    console.error("Error en loadParqueZonificacionIndustriaDetalle:", err);
    if (msgCont) {
      msgCont.innerHTML = "<p>Error al cargar la información.</p>";
    }
  }
}


async function loadParqueLoteDetalle(popupEl, id_intervencion, id_lote_infra) {

  const cont = popupEl.querySelector("#data-parque-lote-detalle");
  if (!cont) return;

  cont.innerHTML = "<em>Cargando información...</em>";

  const fmt = (v) => (v === null || v === undefined || v === "" ? "-" : v);
  const fmtNumber = (v) => {
    if (v === null || v === undefined || v === "") return "-";
    const n = Number(v);
    return isNaN(n)
      ? v
      : n.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  try {
    const response = await parquesIndustriales.getDetalleIntervencion(
      id_intervencion,
      id_lote_infra
    );

    const item = response?.items?.[0];
    if (!item) {
      cont.innerHTML = "<p>No se encontraron datos.</p>";
      return;
    }

    const etapa       = fmt(item.Etapa ?? item.etapa);
    const area        = fmtNumber(item.area ?? item.Area);
    const propietario = fmt(item.Empresa_Instalada ?? item.empresa_instalada);
    const estatus     = fmt(item.Estado_Venta ?? item.estado_venta);
    const ocupabilidad = fmt(item.Ocupabilidad ?? item.ocupabilidad);
    const zonificacion = fmt(item.Zonificacion ?? item.zonificacion);
    const uso          = fmt(item.Uso_planificado ?? item.uso);


    cont.innerHTML = `
      <div class="popup-scroll">
        <table class="popup-table"> 
          <tr>
            <td class="popup-td-label">Etapa</td>
            <td class="popup-td-value" colspan="3">${etapa.toUpperCase()}</td>
          </tr>
           <tr>
            <td class="popup-td-label">Area (m2)</td>
            <td class="popup-td-value" colspan="3">${area}</td>
          </tr>
          <tr>
            <td class="popup-td-label">Empresa Instalada</td>
            <td class="popup-td-value" colspan="3">${propietario}</td>
          </tr>
          <tr>
            <td class="popup-td-label">Disponibilidad del lote</td>
            <td class="popup-td-value" colspan="3">${estatus}</td>
          </tr>        
          <tr>
            <td class="popup-td-label">Ocupabilidad del lote</td>
            <td class="popup-td-value" colspan="3">${ocupabilidad}</td>
          </tr>
          <tr>
            <td class="popup-td-label">Zonificación</td>
            <td class="popup-td-value" colspan="3">${zonificacion}</td>
          </tr>
          <tr>
            <td class="popup-td-label">Uso Planificado</td>
            <td class="popup-td-value" colspan="3">${uso}</td>
          </tr> 

        </table>
      </div>
    `;

  } catch (err) {
    console.error("Error en getDetalleIntervencion:", err);
    cont.innerHTML = "<p>Error al cargar la información.</p>";
  }
}

async function loadZonaEconomicaEspecialDetalle(popupEl, id_intervencion) {
  const msgCont = popupEl.querySelector("#msjZonaEconDet");
  const cont = popupEl.querySelector("#data-zona-econ");

  if (msgCont) msgCont.innerHTML = "<em>Cargando información...</em>";
  if (!cont) return;

  try {
    const response = await cachedFetch(popupCache, popupInFlight, `parques|getIntervencionParamDetalle|${id_intervencion}`, () => parquesIndustriales.getIntervencionParamDetalle(id_intervencion), CACHE_TTL_POPUP_MS);
    const item = response?.item;

    if (!item) {
      if (msgCont) msgCont.innerHTML = "<p>No se encontraron datos.</p>";
      return;
    }

    const params = item.lista_param || [];

    // ====================================================
    // 🔹 FUNCIÓN PARA OBTENER POR id_infraestructura
    // ====================================================
    const getById = (idInfra) => {
      const row = params.find((x) => x.id_infraestructura === idInfra);
      return row ? (row.valor_descripcion || "").trim() || "-" : "-";
    };

    // ====================================================
    // MAPEO POR ID
    // ====================================================
    const hectareas = getById(32); // N° de hectáreas (ha)
    const habilitar = getById(33); // N° total de hectáreas a habilitar
    const empresas = getById(34); // Empresas instaladas
    const principales = getById(35); // Principales empresas
    const actividades = getById(36); // Actividades permitidas
    const productos = getById(37); // Productos que exporta

    const departamento = getById(38);
    const provincia = getById(39);
    const distrito = getById(40);
    const direccion = getById(41);

    // ====================================================
    // FOTO POR INTERVENCIÓN
    // ====================================================
    const _mediaBase = (window.__APP_CONFIG__?.MEDIA_BASE_URL || "https://dimici.produce.gob.pe");
    const imgMap = {
      427: `${_mediaBase}/zonas_img/ZED_PAITA.jpg`,
      425: `${_mediaBase}/zonas_img/ZOFRATACNA.jpg`,
      424: `${_mediaBase}/zonas_img/ZED_ILO.jpg`,
      828: `${_mediaBase}/zonas_img/ZED_MATARANI.png`,
    };

    item.foto_url = imgMap[id_intervencion] || "";

    // ====================================================
    // HTML DEL POPUP
    // ====================================================
    cont.innerHTML = `
      <div class="popup-scroll">
        <table style="width:100%; border-collapse:collapse; font-size:13px; table-layout:fixed;">
          <tbody>
            <tr>
              <td rowspan="5" style="width:250px; vertical-align:top; padding-right:10px;">
                <img 
                  id="zonaeco-foto-img"
                  src=""
                  alt="Foto no disponible"
                  style="max-width:100%; height:auto; display:none; border-radius:6px;"
                />
                <span id="zonaeco-foto-placeholder" style="font-size:12px; color:#777;">
                  (Foto no disponible)
                </span>
              </td>

              <td class="popup-td-label">Zona Económica</td>
              <td class="popup-td-value" colspan="2">${item?.nombre_proyecto || "-"}</td>
            </tr>

            <tr>
              <td class="popup-td-label">Departamento</td>
              <td class="popup-td-value" colspan="2">${departamento}</td>
            </tr>
            <tr>
              <td class="popup-td-label">Provincia</td>
              <td class="popup-td-value" colspan="2">${provincia}</td>
            </tr>
            <tr>
              <td class="popup-td-label">Distrito</td>
              <td class="popup-td-value" colspan="2">${distrito}</td>
            </tr>
            <tr>
              <td class="popup-td-label">Dirección</td>
              <td class="popup-td-value" colspan="2">${direccion}</td>
            </tr>

            <tr><td colspan="4"><hr/></td></tr>

            <tr>
              <td class="popup-td-label">Área total (ha)</td>
              <td class="popup-td-value" colspan="2">${hectareas}</td>
            </tr>
            <tr>
              <td class="popup-td-label">Área habilitable (ha)</td>
              <td class="popup-td-value" colspan="2">${habilitar}</td>
            </tr>
            <tr>
              <td class="popup-td-label">Empresas instaladas</td>
              <td class="popup-td-value" colspan="2">${empresas}</td>
            </tr>
            <tr>
              <td class="popup-td-label">Principales empresas</td>
              <td class="popup-td-value" colspan="3">${principales}</td>
            </tr>
            <tr>
              <td class="popup-td-label">Actividades permitidas</td>
              <td class="popup-td-value" colspan="3">${actividades}</td>
            </tr>
            <tr>
              <td class="popup-td-label">Productos que exporta</td>
              <td class="popup-td-value" colspan="3">${productos}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    // ====================================================
    // FOTO
    // ====================================================
    const img = popupEl.querySelector("#zonaeco-foto-img");
    const ph = popupEl.querySelector("#zonaeco-foto-placeholder");

    if (item.foto_url && img && ph) {
      img.src = item.foto_url;
      img.style.display = "block";
      ph.style.display = "none";
    }

    if (msgCont) msgCont.innerHTML = "";

  } catch (err) {
    console.error("Error en loadZonaEconomicaEspecialDetalle:", err);
    if (msgCont) msgCont.innerHTML = "<p>Error al cargar la información.</p>";
  }
}


async function loadParquesIndustrialesDetalle(popupEl, id_intervencion) {
  const msgCont = popupEl.querySelector("#msjParquesIndustrialesDet");
  const cont = popupEl.querySelector("#data-parques-indust");

  if (msgCont) {
    msgCont.innerHTML = "<em>Cargando información...</em>";
  }
  if (!cont) return;

  try {
    const [response, imgResponse] = await Promise.all([
      cachedFetch(popupCache, popupInFlight, `parques|getIntervencionParamDetalle|${id_intervencion}|0`, () => parquesIndustriales.getIntervencionParamDetalle(id_intervencion, 0), CACHE_TTL_POPUP_MS),
      cachedFetch(popupCache, popupInFlight, `parques|listarImagenesParque|${id_intervencion}`, () => parquesIndustriales.listarImagenesParque(id_intervencion), CACHE_TTL_POPUP_MS)
    ]);
    const item = response?.item;

    if (!item) {
      if (msgCont) msgCont.innerHTML = "<p>No se encontraron datos.</p>";
      return;
    }

    const params = item.lista_param || [];

    // ============================
    //  Helper para leer por id_infraestructura
    // ============================
    const getValue = (idInfra) => {
      const row = params.find((x) => x.id_infraestructura === idInfra);
      let val = (row?.valor_descripcion || "").trim();
      return val || "-";
    };

    // ============================
    //  Valores por ID
    // ============================
    const hectareasTotales = getValue(32); // N° de hectáreas (ha)
    const hectareasAHabilitar = getValue(33); // N° total de hectáreas (ha) a habilitar
    const empresasInstaladas = getValue(34);
    const principalesEmpresas = getValue(35);
    const actividadesPermitidas = getValue(36);
    const productosExporta = getValue(37);
    const departamento = getValue(38);
    const provincia = getValue(39);
    const distrito = getValue(40);
    const direccion = getValue(41);
    const inversion = getValue(43);
    const puestosTrabajo = getValue(44);
    const exportaciones = getValue(45);
    const empresasAInstalar = getValue(46);
    const totalEtapas = getValue(47);
    const etapasHabilitadas = getValue(48);
    const lotesHabilitados = getValue(51);
    const lotesDisponibles = getValue(53);
    const hectareasHabilitadas = getValue(54); // N° total de hectáreas habilitadas (ha)

    // ============================
    //  IMÁGENES DINÁMICAS (Desde API)
    // ============================
    /* 
    const imageSets = {
      // 403 – PI ANCÓN
      403: [
        "https://dimici.produce.gob.pe/zonas_img/PI ANCON.png",
      ],
      // 404 – PI INDUPARK
      404: [
        "https://dimici.produce.gob.pe/zonas_img/indupark1.png",
        "https://dimici.produce.gob.pe/zonas_img/indupark2.png",
        "https://dimici.produce.gob.pe/zonas_img/indupark3.png",
        "https://dimici.produce.gob.pe/zonas_img/indupark4.png",
        "https://dimici.produce.gob.pe/zonas_img/indupark5.png"
      ],
      // 405 – PI SECTOR 62
      405: [
        "https://dimici.produce.gob.pe/zonas_img/sector62_1.png",
        "https://dimici.produce.gob.pe/zonas_img/sector62_2.png",
        "https://dimici.produce.gob.pe/zonas_img/sector62_3.png",
        "https://dimici.produce.gob.pe/zonas_img/sector62_4.png"
      ],
      // 406 – PI LA CHUTANA
      406: [
        "https://dimici.produce.gob.pe/zonas_img/chutana1.png",
        "https://dimici.produce.gob.pe/zonas_img/chutana2.png",
        "https://dimici.produce.gob.pe/zonas_img/chutana3.png",
        "https://dimici.produce.gob.pe/zonas_img/chutana4.png",
        "https://dimici.produce.gob.pe/zonas_img/chutana5.png"
      ],
      830: [
        "https://dimici.produce.gob.pe/zonas_img/piura_futura_1.jpg",
        "https://dimici.produce.gob.pe/zonas_img/piura_futura_2.png",
        "https://dimici.produce.gob.pe/zonas_img/piura_futura_3.png"
      ]
    };
    */

    const images = (imgResponse?.items || []).map(img => img.url);

    // ============================
    //  ARMADO DINÁMICO DE FILAS
    // ============================


    // --- Hectáreas
    const rowsHectareas = `
  ${makeRow("N° de hectáreas (ha)", hectareasTotales)}
  ${makeRow("N° total de hectáreas habilitadas (ha)", hectareasHabilitadas)}
  ${makeRow("N° total de hectáreas (ha) a habilitar", hectareasAHabilitar)}
`;

    // --- Etapas y lotes
    const rowsEtapas = `
  ${makeRow("Número total de etapas", totalEtapas)}
  ${makeRow("Número total de etapas habilitadas", etapasHabilitadas)}
  ${makeRow("Número de lotes habilitados", lotesHabilitados)}
  ${makeRow("Número total de lotes disponibles", lotesDisponibles)}
`;

    // --- Empresas
    const rowsEmpresas = `
  ${makeRow("Empresas instaladas", empresasInstaladas)}
  ${makeRow("Empresas a instalar", empresasAInstalar)}
  ${makeRow("Principales empresas", principalesEmpresas)}
  ${makeRow("Actividades permitidas", actividadesPermitidas)}
  ${makeRow("Productos que exporta", productosExporta)}
`;

    // --- Inversión
    const rowsInversion = `
  ${makeRow("Inversión estimada en la habilitación", inversion)}
  ${makeRow("Puestos de trabajo a generar", puestosTrabajo)}
  ${makeRow("Exportaciones", exportaciones)}
`;

    // ============================
    //  HTML FINAL — layout stacked (imagen arriba, info abajo). Reemplaza
    //  el viejo layout <table> con imagen a la izquierda que colapsaba mal
    //  cuando el popup tenía menos de ~600px.
    // ============================
    const kv = (label, value) => {
      if (!value || value === "-" || String(value).trim() === "") return "";
      return `
        <div class="parq-popup-kv">
          <span class="parq-popup-kv-label">${label}</span>
          <span class="parq-popup-kv-value">${value}</span>
        </div>
      `;
    };
    const section = (title, html) => {
      if (!html.trim()) return "";
      return `
        <div class="parq-popup-section">
          <h5 class="parq-popup-section-title">${title}</h5>
          <div class="parq-popup-grid">${html}</div>
        </div>
      `;
    };

    const nombre = item?.nombre_proyecto || item?.zonificacion_infra || "Parque industrial";

    cont.innerHTML = `
      <div class="parq-popup">
        <div class="parq-popup-media">
          <img id="parqindus-carousel-main-img" class="parq-popup-img" alt="Imagen del parque industrial" />
          <div class="parq-popup-controls">
            <button class="parq-popup-nav" id="parqindus-carousel-prev" type="button" aria-label="Anterior">‹</button>
            <button class="parq-popup-nav" id="parqindus-carousel-next" type="button" aria-label="Siguiente">›</button>
          </div>
        </div>
        <div class="parq-popup-title">${nombre}</div>
        <div class="parq-popup-body">
          ${section(
            "Ubicación",
            `${kv("Departamento", departamento)}
             ${kv("Provincia", provincia)}
             ${kv("Distrito", distrito)}
             ${kv("Dirección", direccion)}`
          )}
          ${section(
            "Hectáreas",
            `${kv("Totales (ha)", hectareasTotales)}
             ${kv("Habilitadas (ha)", hectareasHabilitadas)}
             ${kv("A habilitar (ha)", hectareasAHabilitar)}`
          )}
          ${section(
            "Etapas y lotes",
            `${kv("Total etapas", totalEtapas)}
             ${kv("Etapas habilitadas", etapasHabilitadas)}
             ${kv("Lotes habilitados", lotesHabilitados)}
             ${kv("Lotes disponibles", lotesDisponibles)}`
          )}
          ${section(
            "Empresas y actividad",
            `${kv("Empresas instaladas", empresasInstaladas)}
             ${kv("Empresas a instalar", empresasAInstalar)}
             ${kv("Principales empresas", principalesEmpresas)}
             ${kv("Actividades permitidas", actividadesPermitidas)}
             ${kv("Productos que exporta", productosExporta)}`
          )}
          ${section(
            "Inversión y empleo",
            `${kv("Inversión estimada", inversion)}
             ${kv("Puestos de trabajo", puestosTrabajo)}
             ${kv("Exportaciones", exportaciones)}`
          )}
        </div>
      </div>
`;

    // ============================
    //  LÓGICA DEL CARRUSEL
    // ============================
    const mediaWrap = popupEl.querySelector(".parq-popup-media");
    const mainImg = popupEl.querySelector("#parqindus-carousel-main-img");
    const btnPrev = popupEl.querySelector("#parqindus-carousel-prev");
    const btnNext = popupEl.querySelector("#parqindus-carousel-next");

    if (images.length > 0 && mainImg) {
      let index = 0;
      mainImg.src = images[0];
      mainImg.onclick = () => window.zoomImage && window.zoomImage(mainImg.src);

      // Si hay una sola imagen, ocultamos los nav buttons.
      if (images.length === 1) {
        if (btnPrev) btnPrev.style.display = "none";
        if (btnNext) btnNext.style.display = "none";
      } else {
        if (btnPrev) {
          btnPrev.onclick = () => {
            index = (index - 1 + images.length) % images.length;
            mainImg.src = images[index];
          };
        }
        if (btnNext) {
          btnNext.onclick = () => {
            index = (index + 1) % images.length;
            mainImg.src = images[index];
          };
        }
      }
    } else if (mediaWrap) {
      // Sin imágenes: ocultamos toda la sección de media (no solo el img).
      mediaWrap.style.display = "none";
    }

    if (msgCont) msgCont.innerHTML = "";

  } catch (err) {
    console.error("Error en loadParquesIndustrialesDetalle:", err);
    if (msgCont) msgCont.innerHTML = "<p>Error al cargar la información.</p>";
  }
}


// Crea una fila <tr> solo si hay dato
function makeRow(label, value, colspan = 5) {
  if (!value || value === "-" || value.trim() === "") return "";
  return ` 
    <tr>
      <td class="popup-td-label" >${label}</td>
      <td class="popup-td-value" colspan="${colspan}">${value}</td>
    </tr>
  `;
}

function makeSection(title, rowsHtml) {
  if (!rowsHtml.trim()) return "";
  return `
   <tr></tr>
    <tr>
      <td class="popup-td-section" colspan="4"><strong>${title}</strong></td>
    </tr>
    ${rowsHtml}
  `;
}

function getHaloConfigByLayerId(layerId) {
  return HALO_LAYER_CONFIG[layerId] || HALO_LAYER_CONFIG[4];
}

function renderThumbGallery(popupEl, images) {
  const mainImg = popupEl.querySelector("#zonind-main-img");
  const empty = popupEl.querySelector("#zonind-empty");
  const thumbs = popupEl.querySelector("#zonind-thumbs");

  if (!mainImg || !empty || !thumbs) return;

  // ─────────────
  // Sin imágenes
  // ─────────────
  if (!Array.isArray(images) || images.length === 0) {
    mainImg.style.display = "none";
    thumbs.style.display = "none";
    empty.style.display = "block";
    empty.textContent = "(Foto no disponible)";
    return;
  }

  // ─────────────
  // Imagen principal
  // ─────────────
  let currentIndex = 0;
  mainImg.src = images[0];
  mainImg.style.display = "block";
  empty.style.display = "none";

  // zoom opcional
  mainImg.style.cursor = "zoom-in";
  mainImg.onclick = () => {
    if (window.zoomImage) window.zoomImage(mainImg.src);
  };

  // ─────────────
  // Si solo hay 1 imagen → no miniaturas
  // ─────────────
  if (images.length === 1) {
    thumbs.style.display = "none";
    return;
  }

  // ─────────────
  // Construir miniaturas
  // ─────────────
  thumbs.innerHTML = images
    .map(
      (url, i) => `
        <img
          src="${url}"
          class="zonind-thumb ${i === 0 ? "active" : ""}"
          data-index="${i}"
          alt="Miniatura ${i + 1}"
        />
      `
    )
    .join("");

  thumbs.style.display = "flex";

  // ─────────────
  // Click en miniatura (delegación)
  // ─────────────
  thumbs.onclick = (e) => {
    const thumb = e.target;
    if (!thumb.classList.contains("zonind-thumb")) return;

    const idx = Number(thumb.dataset.index);
    if (Number.isNaN(idx)) return;

    currentIndex = idx;
    mainImg.src = images[currentIndex];

    thumbs
      .querySelectorAll(".zonind-thumb")
      .forEach((t) => t.classList.remove("active"));

    thumb.classList.add("active");
  };
}


export default ArcGISFeatureLayer;
